import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Image as ImageIcon, Video, UploadCloud, CheckCircle2, AlertCircle, 
  Sparkles, Camera, Sun, Move, Palette, Clock, 
  ShieldCheck, X, Plus, BookOpen, Tag, ChevronDown, ChevronRight, RotateCcw, Check
} from 'lucide-react';
import { Database } from '../types/database.types';
import {
  buildFlightScenes,
  buildPathAnchorFrames,
  drawScenesOverlay,
  drawPathOnImage,
  densifyStrokeForExport,
  sampleTimedWaypoints,
  type Point2D,
  type FlightScene,
} from '../utils/droneFlightPath';
import { Link } from 'react-router-dom';
import MediaSourceButtons, { ImageDropZone, useGalleryMediaIngest } from '../components/MediaSourceButtons';
import ModalOverlay from '../components/ui/ModalOverlay';
import { dataUrlToFile, uploadToGallery } from '../lib/mediaGallery';
import { asyncJobsEnabled } from '../lib/supabase';
import {
  clearCreationVideoJob,
  dismissVideoJob,
  jobStatusLabel,
  listActiveVideoJobs,
  rememberCreationVideoJob,
  toMediaUrl,
  waitForJob,
} from '../lib/jobsApi';
import type { JobRow } from '../types/jobs';
import { useGenerationToastOptional } from '../contexts/GenerationToastContext';
import VideoModelPicker, {
  type VideoModelPickerValue,
} from '../components/video/VideoModelPicker';
import { estimateCredits, pickVideoModelSelection, rateForGeneratedAudio, supportsGeneratedAudio, supportsMultiImageReference, uiMaxReferenceImages } from '../lib/videoModelCatalog';
import { useFeatureVideoPick } from '../lib/videoFeatureUsage';
import ImageModelPicker, { defaultImageModel, pickImageModel } from '../components/image/ImageModelPicker';
import ReferenceImageTray, { type ImageRefRole, type ReferenceImageItem } from '../components/ReferenceImageTray';
import StagingHistoryRail from '../components/StagingHistoryRail';
import {
  appendStagingHistory,
  listStagingHistory,
  STAGING_HISTORY_MAX,
  type StagingHistoryEntry,
  type StagingHistoryTag,
} from '../lib/stagingHistory';

type CreationMode = 'staging' | 'drone';

type BackgroundVideoJob = {
  id: string;
  label: string;
  status: string;
  step: string;
};

function statusProgress(status: string): number {
  if (status === 'pending' || status === 'queued') return 18;
  if (status === 'submitted') return 42;
  if (status === 'processing') return 72;
  if (status === 'completed') return 100;
  return 10;
}

// ======= SCENE TAG SYSTEM =======
interface SceneTag {
  id: string;
  category: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  active: boolean;
  prompt_json?: Record<string, any>;
}

interface StoryboardPhase {
  label: string;
  description: string;
  prompt_en?: string;
  /** 1-based index into the reference images sent to Gemini / the video model. */
  image_index?: number;
  image_url?: string;
  loading?: boolean;
}

const TAG_CATEGORIES: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  camera: { icon: <Camera size={12} />, color: '#6366f1', label: 'Câmera' },
  transformation: { icon: <Sparkles size={12} />, color: '#a855f7', label: 'Transição' },
  lighting: { icon: <Sun size={12} />, color: '#f59e0b', label: 'Iluminação' },
  movement: { icon: <Move size={12} />, color: '#06b6d4', label: 'Movimento' },
  style: { icon: <Palette size={12} />, color: '#ec4899', label: 'Estilo' },
  timing: { icon: <Clock size={12} />, color: '#10b981', label: 'Timing' },
  constraints: { icon: <ShieldCheck size={12} />, color: '#ef4444', label: 'Restrições' },
  distance: { icon: <Move size={12} />, color: '#f43f5e', label: 'Distância' },
};

const ICON_MAP: Record<string, React.ReactNode> = {
  camera: <Camera size={12} />,
  sparkles: <Sparkles size={12} />,
  sun: <Sun size={12} />,
  move: <Move size={12} />,
  palette: <Palette size={12} />,
  clock: <Clock size={12} />,
  'shield-check': <ShieldCheck size={12} />,
  tag: <Tag size={12} />,
};

// Convert DB row to UI SceneTag
function dbTagToSceneTag(row: Database['public']['Tables']['scene_tags']['Row']): SceneTag {
  const catInfo = TAG_CATEGORIES[row.category];
  return {
    id: row.tag_key,
    category: row.category,
    label: row.label,
    icon: ICON_MAP[row.icon_name] || <Tag size={12} />,
    color: row.color || catInfo?.color || '#6366f1',
    active: false,
    prompt_json: row.prompt_json as Record<string, any>,
  };
}

function newRefId() {
  return `ref-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function applyAnalyzeTagResult(
  prev: SceneTag[],
  active_tags: unknown,
  new_tags: unknown,
): SceneTag[] {
  let updated = prev.map((t) => ({
    ...t,
    active: t.active || (Array.isArray(active_tags) && active_tags.includes(t.id)),
  }));
  if (Array.isArray(new_tags)) {
    for (const nt of new_tags as Array<{ id?: string; category?: string; label?: string }>) {
      if (!nt?.id || updated.find((t) => t.id === nt.id)) continue;
      const catInfo = TAG_CATEGORIES[nt.category as keyof typeof TAG_CATEGORIES];
      if (!catInfo) continue;
      updated.push({
        id: nt.id,
        category: nt.category || 'style',
        label: nt.label || nt.id,
        icon: catInfo.icon,
        color: catInfo.color,
        active: true,
      });
    }
  }
  // Cinemático e Ultra Realista são exclusivos — última menção em active_tags vence.
  const styleIds = ['style_cinematic', 'style_realistic'] as const;
  if (Array.isArray(active_tags)) {
    let winner: string | null = null;
    for (const id of active_tags) {
      if (styleIds.includes(id as typeof styleIds[number])) winner = String(id);
    }
    if (winner) {
      updated = updated.map((t) =>
        styleIds.includes(t.id as typeof styleIds[number])
          ? { ...t, active: t.id === winner }
          : t,
      );
    }
  } else {
    const both = styleIds.filter((id) => updated.find((t) => t.id === id)?.active);
    if (both.length > 1) {
      updated = updated.map((t) =>
        styleIds.includes(t.id as typeof styleIds[number])
          ? { ...t, active: t.id === 'style_cinematic' }
          : t,
      );
    }
  }
  return updated;
}

function serializeHistoryTags(list: SceneTag[]): StagingHistoryTag[] {
  return list.map(({ id, category, label, color, active, prompt_json }) => ({
    id,
    category,
    label,
    color,
    active,
    prompt_json,
  }));
}

function mergeRestoredTags(catalog: SceneTag[], saved: StagingHistoryTag[]): SceneTag[] {
  const byId = new Map(catalog.map((t) => [t.id, t]));
  const restored: SceneTag[] = saved.map((ht) => {
    const existing = byId.get(ht.id);
    if (existing) return { ...existing, active: ht.active };
    const catInfo = TAG_CATEGORIES[ht.category];
    return {
      id: ht.id,
      category: ht.category,
      label: ht.label,
      icon: catInfo?.icon || ICON_MAP.tag || <Tag size={12} />,
      color: ht.color || catInfo?.color || '#6366f1',
      active: ht.active,
      prompt_json: ht.prompt_json,
    };
  });
  const restoredIds = new Set(restored.map((t) => t.id));
  const extras = catalog.filter((t) => !restoredIds.has(t.id)).map((t) => ({ ...t, active: false }));
  return [...restored, ...extras];
}

export default function CreationPanel() {
  const { user } = useAuth();
  const generationToast = useGenerationToastOptional();
  
  // Mode selector
  const [creationMode, setCreationMode] = useState<CreationMode>('staging');

  // Drone POV states
  const [droneReferences, setDroneReferences] = useState<Array<{ id: string; image_b64: string; label: string; role?: ImageRefRole }>>([]);
  const droneCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dronePoints, setDronePoints] = useState<Point2D[]>([]);
  const [droneApproachPoint, setDroneApproachPoint] = useState<Point2D | null>(null);
  const [droneDrawMode, setDroneDrawMode] = useState<'path' | 'approach'>('path');

  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');

  // Prompt 1: Decoração (para gerar previews)
  const [decorPrompt, setDecorPrompt] = useState<string>('');
  // Prompt 2: Vídeo (para gerar o vídeo com tags)
  const [videoPrompt, setVideoPrompt] = useState<string>('');
  
  const [startFrameB64, setStartFrameB64] = useState<string>('');
  const [endFrameB64, setEndFrameB64] = useState<string>('');
  const [stagingRefs, setStagingRefs] = useState<ReferenceImageItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'fin_ops' | 'employee' | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Array<{id: string; label: string; image_b64: string; prompt: string}>>([]);
  const [selectedPreview, setSelectedPreview] = useState<number | null>(null);
  const [duration, setDuration] = useState<number>(5);
  const {
    value: droneVideoPick,
    setValue: setDroneVideoPick,
    recommendedModelId: droneRecommendedModelId,
    useCount: droneModelUseCount,
  } = useFeatureVideoPick('flight_plan', 'i2v');
  const {
    value: stagingVideoPick,
    setValue: setStagingVideoPick,
    recommendedModelId: stagingRecommendedModelId,
    useCount: stagingModelUseCount,
  } = useFeatureVideoPick('staging', 'i2v');
  const [finalVideo, setFinalVideo] = useState<string | null>(null);
  const [tags, setTags] = useState<SceneTag[]>([]);
  const [analyzingTags, setAnalyzingTags] = useState<boolean>(false);
  const [storyboard, setStoryboard] = useState<StoryboardPhase[]>([]);
  const [historyEntries, setHistoryEntries] = useState<StagingHistoryEntry[]>([]);
  const [historyActiveId, setHistoryActiveId] = useState<string | null>(null);
  const [renderingVideo, setRenderingVideo] = useState<boolean>(false);
  const [renderingElapsed, setRenderingElapsed] = useState<number>(0);
  const [fastForwardActive, setFastForwardActive] = useState<boolean>(false);
  const [pendingVideoUrl, setPendingVideoUrl] = useState<string | null>(null);
  const [, setActiveJobId] = useState<string | null>(null);
  const [jobDeliveredToGallery, setJobDeliveredToGallery] = useState(false);
  const [bgVideoJobs, setBgVideoJobs] = useState<BackgroundVideoJob[]>([]);
  const trackedJobIdsRef = useRef<Set<string>>(new Set());
  const dismissBgJob = useCallback((jobId: string) => {
    dismissVideoJob(jobId);
    trackedJobIdsRef.current.delete(jobId);
    setBgVideoJobs((prev) => prev.filter((j) => j.id !== jobId));
    setActiveJobId((cur) => (cur === jobId ? null : cur));
  }, []);

  const trackBackgroundVideoJob = useCallback((jobId: string, label: string, seed?: JobRow) => {
    if (trackedJobIdsRef.current.has(jobId)) return;
    trackedJobIdsRef.current.add(jobId);
    rememberCreationVideoJob(jobId);
    setActiveJobId(jobId);
    setBgVideoJobs((prev) => [
      ...prev.filter((j) => j.id !== jobId),
      {
        id: jobId,
        label,
        status: seed?.status || 'queued',
        step: seed ? jobStatusLabel(seed) : 'Na fila...',
      },
    ]);

    void waitForJob(jobId, {
      timeoutMs: 20 * 60 * 1000,
      onStatus: (j) => {
        setBgVideoJobs((prev) =>
          prev.map((row) =>
            row.id === jobId
              ? { ...row, status: j.status, step: jobStatusLabel(j) }
              : row,
          ),
        );
      },
    })
      .then((job) => {
        trackedJobIdsRef.current.delete(jobId);
        setBgVideoJobs((prev) => prev.filter((j) => j.id !== jobId));
        clearCreationVideoJob();
        setJobDeliveredToGallery(true);
        setActiveJobId((cur) => (cur === jobId ? null : cur));
        const url = toMediaUrl(job.result_url);
        if (url && generationToast) {
          generationToast.pushCompletionToast({
            id: `job-${jobId}`,
            title: 'Vídeo pronto',
            message: `${label} ficou pronto. Clique para visualizar.`,
            mediaUrl: url,
            mediaType: 'video',
          });
        }
      })
      .catch((err: any) => {
        trackedJobIdsRef.current.delete(jobId);
        setBgVideoJobs((prev) =>
          prev.map((row) =>
            row.id === jobId
              ? { ...row, status: 'failed', step: err?.message || 'Falha no job' }
              : row,
          ),
        );
        setActiveJobId((cur) => (cur === jobId ? null : cur));
        clearCreationVideoJob();
      });
  }, [generationToast]);

  const [hiddenTagIds, setHiddenTagIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('hidden_tags');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('collapsed_tag_cats');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  // Image transformation modal states
  const [isTransformModalOpen, setIsTransformModalOpen] = useState(false);
  const [transformSource, setTransformSource] = useState<'start' | 'end' | string | null>(null);
  const [transformPrompt, setTransformPrompt] = useState('');
  const [transformingImage, setTransformingImage] = useState(false);
  const [savingTransform, setSavingTransform] = useState(false);
  const [transformError, setTransformError] = useState<string | null>(null);
  const [tempTransformedImage, setTempTransformedImage] = useState<string>('');

  const [imageModelId, setImageModelId] = useState<string>(defaultImageModel().id);
  const selectedImageModel = useMemo(() => pickImageModel(imageModelId), [imageModelId]);
  const previewCredits = selectedImageModel.creditsPerImage * 3;
  const transformCredits = selectedImageModel.creditsPerImage;

  const videoModelBody = (pick: VideoModelPickerValue, feature: 'staging' | 'flight_plan') => ({
    video_model_id: pick.videoModelId,
    resolution_id: pick.resolutionId,
    feature,
    generate_audio: Boolean(pick.generateAudio),
  });

  const estimateI2vPick = (pick: VideoModelPickerValue) => {
    const sel = pickVideoModelSelection({
      videoModelId: pick.videoModelId,
      resolutionId: pick.resolutionId,
      modality: 'i2v',
    });
    const rate = rateForGeneratedAudio(
      sel.resolution,
      Boolean(pick.generateAudio) && supportsGeneratedAudio(sel.model),
    );
    return estimateCredits({
      creditsPerSecond: rate.creditsPerSecond,
      durationSeconds: duration,
    });
  };

  const stagingCreditsEstimate = estimateI2vPick(stagingVideoPick);
  const droneCreditsEstimate = estimateI2vPick(droneVideoPick);

  const stagingVideoModel = pickVideoModelSelection({
    videoModelId: stagingVideoPick.videoModelId,
    resolutionId: stagingVideoPick.resolutionId,
    modality: 'i2v',
  }).model;
  const droneVideoModel = pickVideoModelSelection({
    videoModelId: droneVideoPick.videoModelId,
    resolutionId: droneVideoPick.resolutionId,
    modality: 'i2v',
  }).model;
  const stagingMultiRef = supportsMultiImageReference(stagingVideoModel);
  const droneMultiRef = supportsMultiImageReference(droneVideoModel);
  const stagingMaxRefs = uiMaxReferenceImages(stagingVideoModel);
  const droneMaxExtras = Math.max(0, uiMaxReferenceImages(droneVideoModel) - 1);

  const applyStagingVideoPick = (next: VideoModelPickerValue) => {
    setStagingVideoPick(next);
    setDuration(next.durationSeconds);
    const nextModel = pickVideoModelSelection({
      videoModelId: next.videoModelId,
      resolutionId: next.resolutionId,
      modality: 'i2v',
    }).model;
    const max = uiMaxReferenceImages(nextModel);
    const multi = supportsMultiImageReference(nextModel);
    setStagingRefs((prev) => {
      const kept = prev.slice(0, max);
      if (multi || kept.length === 0) return kept;
      return kept.map((r, i) => ({
        ...r,
        role: i === 0 ? 'start' : i === kept.length - 1 ? 'end' : null,
      }));
    });
  };

  const stagingBaseB64 =
    stagingRefs.find((r) => r.role === 'start')?.image_b64 || stagingRefs[0]?.image_b64 || '';
  const stagingEndB64 = stagingRefs.find((r) => r.role === 'end')?.image_b64 || '';

  const stagingReferencePayload = stagingRefs.map((r, i) => ({
    image_b64: r.image_b64,
    role: r.role === 'start' ? 'start' : r.role === 'end' ? 'end' : 'ref',
    label: r.role === 'start' ? 'Inicial' : r.role === 'end' ? 'Final' : (r.label || `Referência ${i + 1}`),
  }));

  const setStagingRole = (id: string, role: ImageRefRole) => {
    setStagingRefs((prev) =>
      prev.map((r) => {
        if (r.id === id) return { ...r, role };
        if (role && r.role === role) return { ...r, role: null };
        return r;
      }),
    );
  };

  const addStagingImage = async (rawB64: string, title?: string) => {
    const cleanB64 = await processImageToCleanJpeg(rawB64);
    let rejected = false;
    setStagingRefs((prev) => {
      if (prev.length >= stagingMaxRefs) {
        rejected = true;
        return prev;
      }
      const role: ImageRefRole = !stagingMultiRef
        ? (prev.some((r) => r.role === 'start') || prev.length > 0 ? 'end' : 'start')
        : null;
      const withoutEnd = !stagingMultiRef && role === 'end' ? prev.filter((r) => r.role !== 'end') : prev;
      return [...withoutEnd, { id: newRefId(), image_b64: cleanB64, role, label: title }];
    });
    if (rejected) {
      setError(`Máximo de ${stagingMaxRefs} imagens neste modelo.`);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      if (ratio > 1.25) setAspectRatio('16:9');
      else if (ratio < 0.8) setAspectRatio('9:16');
      else setAspectRatio('1:1');
    };
    img.src = cleanB64;
  };

  const removeStagingImage = (id: string) => {
    setStagingRefs((prev) => prev.filter((r) => r.id !== id));
  };

  const getImageAspectRatio = (b64: string): Promise<'16:9' | '9:16' | '1:1'> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio > 1.25) {
          resolve('16:9');
        } else if (ratio < 0.8) {
          resolve('9:16');
        } else {
          resolve('1:1');
        }
      };
      img.onerror = () => resolve('16:9'); // fallback
      img.src = b64;
    });
  };

  const chatEndRef = useRef<HTMLDivElement>(null);
  const decorTextareaRef = useRef<HTMLTextAreaElement>(null);
  const videoTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Derived state for video rendering phase progress (using an elegant asymptotic curve so it never gets stuck at 99%)
  const tau = duration === 5 ? 50 : 90;
  const overallProgress = fastForwardActive
    ? Math.min(100, Math.round((1 - Math.exp(-renderingElapsed / tau)) * 100))
    : Math.min(99, Math.round((1 - Math.exp(-renderingElapsed / tau)) * 100));
  
  const activePhaseIdx = storyboard.length > 0 
    ? Math.min(storyboard.length - 1, Math.floor(overallProgress / (100 / storyboard.length)))
    : -1;
    
  const activePhaseProgress = storyboard.length > 0
    ? (overallProgress === 100 
        ? 100 
        : Math.min(99, Math.round(((overallProgress % (100 / storyboard.length)) / (100 / storyboard.length)) * 100)))
    : 0;

  // Fetch user role and organization
  useEffect(() => {
    async function loadUserRole() {
      if (user) {
        const { data } = await supabase
          .from('organization_members')
          .select('role, org_id')
          .eq('user_id', user.id)
          .single();

        if (data) {
          setUserRole(data.role as 'admin' | 'fin_ops' | 'employee');
          setOrgId(data.org_id);
        }
      }
    }
    loadUserRole();
  }, [user]);

  // Slim progress bar: track in-flight video jobs without blocking the form.
  useEffect(() => {
    if (!asyncJobsEnabled || !orgId) return;
    let cancelled = false;

    (async () => {
      try {
        const videoJobs = await listActiveVideoJobs(orgId, { userId: user?.id, limit: 10 });
        if (cancelled || !videoJobs.length) return;
        for (const job of videoJobs) {
          const label =
            job.modality === 'v2v'
              ? 'V2V'
              : 'Vídeo';
          trackBackgroundVideoJob(job.id, label, job);
        }
      } catch (err: any) {
        if (!cancelled) console.warn('hydrate jobs', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, user?.id, trackBackgroundVideoJob]);

  useEffect(() => {
    if (!user?.id || !orgId) return;
    let cancelled = false;
    listStagingHistory(user.id, orgId).then((entries) => {
      if (!cancelled) setHistoryEntries(entries);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id, orgId]);

  // Timer for visual progress per storyboard phase during rendering
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    
    if (renderingVideo) {
      if (fastForwardActive) {
        // Fast-forward animation: speed up elapsed time significantly
        interval = setInterval(() => {
          setRenderingElapsed(prev => prev + 15);
        }, 80);
      } else {
        // Normal clock: 1 second per second
        interval = setInterval(() => {
          setRenderingElapsed(prev => prev + 1);
        }, 1000);
      }
    } else {
      setRenderingElapsed(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [renderingVideo, fastForwardActive]);

  // Handle visual completion and transition after fast-forward finishes
  useEffect(() => {
    if (fastForwardActive && overallProgress >= 100) {
      const timer = setTimeout(() => {
        setFastForwardActive(false);
        setRenderingVideo(false);
        setFinalVideo(pendingVideoUrl);
        setPendingVideoUrl(null);
        setLoading(false);
        setLoadingStep('');
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [fastForwardActive, overallProgress, pendingVideoUrl]);

  // Fetch tags from DB (excluding org-hidden system tags)
  useEffect(() => {
    async function loadTags() {
      const { data } = await supabase
        .from('scene_tags')
        .select('*')
        .eq('is_active', true)
        .order('category')
        .order('label');
      if (!data || data.length === 0) return;

      // Check if org has hidden any system tags
      let hiddenIds = new Set<string>();
      if (orgId) {
        const { data: hiddenRows } = await supabase
          .from('org_hidden_tags')
          .select('tag_id')
          .eq('org_id', orgId);
        if (hiddenRows) {
          hiddenIds = new Set(hiddenRows.map((r: any) => r.tag_id));
        }
      }

      const filtered = data.filter(t => !hiddenIds.has(t.id));
      setTags(filtered.map(dbTagToSceneTag));
    }
    loadTags();
  }, [orgId]);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [previews, finalVideo, error, loading]);

  // Auto-resize textarea
  const handleTextareaInput = useCallback((ref: React.RefObject<HTMLTextAreaElement | null>) => {
    const el = ref.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  const persistStagingHistory = useCallback(async (overrides?: {
    tags?: SceneTag[];
    storyboard?: StoryboardPhase[];
    prompt?: string;
    images?: ReferenceImageItem[];
  }) => {
    if (!user?.id || !orgId) return;
    const nextStoryboard = overrides?.storyboard ?? storyboard;
    const nextPrompt = (overrides?.prompt ?? videoPrompt).trim();
    if (nextStoryboard.length === 0 || !nextPrompt) return;
    const nextImages = overrides?.images ?? stagingRefs;
    const nextTags = overrides?.tags ?? tags;
    try {
      const result = await appendStagingHistory(user.id, orgId, {
        prompt: nextPrompt,
        images: nextImages.map((img) => ({
          id: img.id,
          image_b64: img.image_b64,
          role: img.role,
          label: img.label,
        })),
        tags: serializeHistoryTags(nextTags),
        storyboard: nextStoryboard.map((p) => ({
          label: p.label,
          description: p.description,
          prompt_en: p.prompt_en,
          image_index: p.image_index,
          image_url: p.image_url,
        })),
        duration,
        aspectRatio,
      });
      if (!result) return;
      setHistoryActiveId(result.entry.id);
      if (result.created) {
        setHistoryEntries((prev) => [result.entry, ...prev.filter((e) => e.id !== result.entry.id)].slice(0, STAGING_HISTORY_MAX));
      }
    } catch (err) {
      console.warn('staging history', err);
    }
  }, [user?.id, orgId, storyboard, videoPrompt, stagingRefs, tags, duration, aspectRatio]);

  const restoreStagingHistory = useCallback((entry: StagingHistoryEntry) => {
    setHistoryActiveId(entry.id);
    setVideoPrompt(entry.prompt);
    setStagingRefs(entry.images.map((img) => ({
      id: img.id || newRefId(),
      image_b64: img.image_b64,
      role: img.role,
      label: img.label,
    })));
    setStoryboard(entry.storyboard.map((p) => ({
      label: p.label,
      description: p.description,
      prompt_en: p.prompt_en,
      image_index: p.image_index,
      image_url: p.image_url,
    })));
    setTags((prev) => mergeRestoredTags(prev, entry.tags));
    if (entry.duration) {
      setDuration(entry.duration);
      setStagingVideoPick((p) => ({ ...p, durationSeconds: entry.duration as number }));
    }
    if (entry.aspectRatio) setAspectRatio(entry.aspectRatio);
    setFinalVideo(null);
    setError(null);
    setGenerationId(null);
    setPreviews([]);
    setSelectedPreview(null);
    requestAnimationFrame(() => handleTextareaInput(videoTextareaRef));
  }, [handleTextareaInput, setStagingVideoPick]);

  const startNewStaging = useCallback(() => {
    setHistoryActiveId(null);
    setFinalVideo(null);
    setJobDeliveredToGallery(false);
    setPreviews([]);
    setStagingRefs([]);
    setDecorPrompt('');
    setVideoPrompt('');
    setGenerationId(null);
    setSelectedPreview(null);
    setTags((prev) => prev.map((t) => ({ ...t, active: false })));
    setStoryboard([]);
    setError(null);
    requestAnimationFrame(() => handleTextareaInput(videoTextareaRef));
  }, [handleTextareaInput]);

  // === AGENTE DE TAGS: Chama analyze-tags com debounce de 800ms ===
  const analyzeTagsFromPrompt = useCallback(async (text: string) => {
    if (text.trim().length < 5) return;
    
    setAnalyzingTags(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('analyze-tags', {
        body: { 
          prompt_text: text,
          start_frame: stagingBaseB64 || startFrameB64,
          end_frame: stagingEndB64 || endFrameB64,
          reference_images: stagingReferencePayload,
          available_tags: tags.map(t => ({ id: t.id, category: t.category, label: t.label, prompt_json: t.prompt_json })),
          active_tags: tags.filter((t) => t.active).map((t) => t.id),
        }
      });

      console.log('Analyze Response:', data);

      if (fnError) {
        console.error('Erro na função Edge:', fnError);
        return;
      }

      if (!data) return;

      const { active_tags, new_tags, storyboard, debug } = data;
      
      if (debug) {
        console.log('AI Debug Info:', debug);
      }

      if (Array.isArray(storyboard)) setStoryboard(storyboard);

      const nextTags = applyAnalyzeTagResult(tags, active_tags, new_tags);
      setTags(nextTags);

      if (Array.isArray(storyboard) && storyboard.length > 0) {
        void persistStagingHistory({ tags: nextTags, storyboard, prompt: text });
      }
    } catch (err) {
      console.error('Erro inesperado ao analisar tags:', err);
    } finally {
      setAnalyzingTags(false);
    }
  }, [stagingBaseB64, stagingEndB64, stagingReferencePayload, startFrameB64, endFrameB64, tags, persistStagingHistory]);

  const rewritePhaseCite = (promptEn: string | undefined, n: number) => {
    const token = `<<<image_${n}>>>`;
    const rewritten = String(promptEn || '')
      .replace(/<<<image_\d+>>>/g, token)
      .replace(/@image_\d+/gi, token);
    return rewritten.includes(token) ? rewritten : `Match the look of ${token}. ${rewritten}`.trim();
  };

  const handleUpdateStoryboardPhase = (
    index: number,
    patch: { description?: string; prompt_en?: string; image_index?: number; image_url?: string },
  ) => {
    setStoryboard((prev) => prev.map((p, i) => {
      if (i !== index) return p;
      const next = { ...p, ...patch };
      if (patch.image_index != null) {
        next.prompt_en = rewritePhaseCite(next.prompt_en, Number(patch.image_index));
      }
      return next;
    }));
  };

  const storyboardImageCatalog = (): string[] => {
    if (stagingRefs.length) return stagingRefs.map((r) => r.image_b64).filter(Boolean);
    return [stagingBaseB64 || startFrameB64, stagingEndB64 || endFrameB64].filter(Boolean);
  };

  const phaseCitedImageIndex = (phase: StoryboardPhase): number | undefined => {
    const fromPrompt = String(phase.prompt_en || '').match(/<<<image_(\d+)>>>/i);
    if (fromPrompt) {
      const n = parseInt(fromPrompt[1], 10);
      if (Number.isFinite(n) && n >= 1) return n;
    }
    const n = parseInt(String(phase.image_index ?? ''), 10);
    return Number.isFinite(n) && n >= 1 ? n : undefined;
  };

  const handleVideoPromptChange = useCallback((text: string) => {
    setVideoPrompt(text);
  }, []);

  const STYLE_EXCLUSIVE = new Set(['style_cinematic', 'style_realistic']);

  const toggleTag = (id: string) => {
    setTags((prev) => {
      const target = prev.find((t) => t.id === id);
      const turningOn = !(target?.active);
      return prev.map((t) => {
        if (t.id === id) return { ...t, active: turningOn };
        if (turningOn && STYLE_EXCLUSIVE.has(id) && STYLE_EXCLUSIVE.has(t.id)) {
          return { ...t, active: false };
        }
        return t;
      });
    });
  };

  const openTransformModal = (source: 'start' | 'end' | string) => {
    setTransformSource(source);
    setTransformPrompt('');
    setTransformError(null);
    setTempTransformedImage('');
    setIsTransformModalOpen(true);
  };

  const transformCurrentImage = (): string => {
    if (transformSource === 'start') return stagingBaseB64 || startFrameB64;
    if (transformSource === 'end') return stagingEndB64 || endFrameB64;
    if (typeof transformSource === 'string') {
      return stagingRefs.find((r) => r.id === transformSource)?.image_b64 || '';
    }
    return '';
  };

  const confirmTransform = async () => {
    if (!tempTransformedImage || savingTransform) return;

    setSavingTransform(true);
    setTransformError(null);
    try {
      const orgId = await getUserOrganizationId();
      if (orgId) {
        const file = dataUrlToFile(
          tempTransformedImage,
          `staging-${transformSource}-${Date.now()}`,
        );
        await uploadToGallery(orgId, file, {
          title: transformSource === 'start'
            ? `Staging frame inicial · IA`
            : transformSource === 'end'
              ? `Staging frame final · IA`
              : `Staging referência · IA`,
        });
      }

      if (transformSource === 'start') {
        setStartFrameB64(tempTransformedImage);
        setStagingRefs((prev) => {
          const idx = prev.findIndex((r) => r.role === 'start');
          if (idx >= 0) return prev.map((r, i) => (i === idx ? { ...r, image_b64: tempTransformedImage } : r));
          if (prev[0]) return prev.map((r, i) => (i === 0 ? { ...r, image_b64: tempTransformedImage } : r));
          return [{ id: newRefId(), image_b64: tempTransformedImage, role: 'start' }];
        });
      } else if (transformSource === 'end') {
        setEndFrameB64(tempTransformedImage);
        setStagingRefs((prev) => {
          const idx = prev.findIndex((r) => r.role === 'end');
          if (idx >= 0) return prev.map((r, i) => (i === idx ? { ...r, image_b64: tempTransformedImage } : r));
          return [...prev, { id: newRefId(), image_b64: tempTransformedImage, role: 'end' }];
        });
      } else if (typeof transformSource === 'string') {
        setStagingRefs((prev) =>
          prev.map((r) => (r.id === transformSource ? { ...r, image_b64: tempTransformedImage } : r)),
        );
      }
      setIsTransformModalOpen(false);
      setTempTransformedImage('');
    } catch (err: any) {
      setTransformError(err?.message || 'Falha ao salvar na galeria.');
    } finally {
      setSavingTransform(false);
    }
  };

  const handleTransformImage = async () => {
    const currentImage = transformCurrentImage();
    if (!currentImage) {
      setTransformError("Nenhuma imagem para transformar.");
      return;
    }
    if (!transformPrompt.trim()) {
      setTransformError("Por favor, descreva a transformação desejada.");
      return;
    }

    setTransformingImage(true);
    setTransformError(null);

    try {
      const orgId = await getUserOrganizationId();
      if (!orgId) throw new Error("Você não está em nenhuma organização.");

      // Detect original image aspect ratio
      const detectedRatio = await getImageAspectRatio(currentImage);

      const { data, error } = await supabase.functions.invoke('create-preview', {
        body: { 
          image_b64: currentImage, 
          prompt: transformPrompt, 
          organization_id: orgId, 
          aspect_ratio: detectedRatio,
          single_image: true,
          image_model_id: imageModelId,
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.async && (data.job_id || data.job_ids)) {
        const resolved = await awaitAsyncPreviews(data);
        const transformedImg = getPreviewImgSrc(resolved.previews[0]);
        setTempTransformedImage(transformedImg);
      } else if (data?.success && data?.previews && data.previews.length > 0) {
        const transformedImg = getPreviewImgSrc(data.previews[0]);
        setTempTransformedImage(transformedImg);
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error("Erro desconhecido ao transformar a imagem.");
      }
    } catch (err: any) {
      setTransformError(err?.message || "Erro na transformação da imagem.");
    } finally {
      setTransformingImage(false);
    }
  };

  const processImageToCleanJpeg = (b64: string, maxDim: number = 2048): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let width = img.naturalWidth;
        let height = img.naturalHeight;
        let ratio = width / height;
        let targetWidth = width;
        let targetHeight = height;
        let offsetX = 0;
        let offsetY = 0;

        // Force aspect ratio within [0.4, 2.5] by padding with white borders (letterbox/pillarbox)
        if (ratio < 0.4) {
          targetWidth = Math.round(height * 0.4);
          offsetX = Math.round((targetWidth - width) / 2);
        } else if (ratio > 2.5) {
          targetHeight = Math.round(width / 2.5);
          offsetY = Math.round((targetHeight - height) / 2);
        }

        // Apply max dimension constraint on target canvas size
        if (targetWidth > maxDim || targetHeight > maxDim) {
          const scale = maxDim / Math.max(targetWidth, targetHeight);
          targetWidth = Math.round(targetWidth * scale);
          targetHeight = Math.round(targetHeight * scale);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          offsetX = Math.round(offsetX * scale);
          offsetY = Math.round(offsetY * scale);
        }

        // Apply min dimension constraint (Kling requires >= 300px)
        const minDim = 300;
        if (targetWidth < minDim || targetHeight < minDim) {
          const scale = minDim / Math.min(targetWidth, targetHeight);
          targetWidth = Math.round(targetWidth * scale);
          targetHeight = Math.round(targetHeight * scale);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          offsetX = Math.round(offsetX * scale);
          offsetY = Math.round(offsetY * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Fill background (white padding)
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, targetWidth, targetHeight);
          ctx.drawImage(img, offsetX, offsetY, width, height);
        }
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.onerror = () => {
        resolve(b64);
      };
      img.src = b64;
    });
  };

  const applyImageToFrame = async (rawB64: string, setter: (v: string) => void) => {
    const cleanB64 = await processImageToCleanJpeg(rawB64);
    setter(cleanB64);
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      if (ratio > 1.25) setAspectRatio('16:9');
      else if (ratio < 0.8) setAspectRatio('9:16');
      else setAspectRatio('1:1');
    };
    img.src = cleanB64;
  };

  const applyDroneReferenceImage = async (rawB64: string, title?: string) => {
    const cleanB64 = await processImageToCleanJpeg(rawB64);
    setDroneReferences((prev) => {
      if (prev.length >= droneMaxExtras) return prev;
      return [...prev, { id: newRefId(), image_b64: cleanB64, label: title || '' }];
    });
  };

  const { ingestFiles: ingestDroneFiles, uploading: droneUploading } = useGalleryMediaIngest({
    onImageSelected: (dataUrl, meta) => applyDroneReferenceImage(dataUrl, meta?.title),
  });

  const getUserOrganizationId = async () => {
    if (!user) return null;
    const { data } = await supabase
      .from('organization_members')
      .select('org_id')
      .eq('user_id', user.id)
      .single();
    return data?.org_id;
  };

  // Helper: extract image src from any preview format
  const getPreviewImgSrc = (p: any): string => {
    if (!p) return '';
    if (typeof p === 'string') return p;
    if (typeof p?.image_b64 === 'string') return p.image_b64;
    if (typeof p?.image_url === 'string') return p.image_url;
    if (typeof p?.url === 'string') return p.url;
    return '';
  };
  const getPreviewLabel = (p: any, i: number): string => {
    if (!p) return `Opção ${i + 1}`;
    if (typeof p === 'string') return `Opção ${i + 1}`;
    if (typeof p?.label === 'string') return p.label;
    return `Opção ${i + 1}`;
  };
  const getPreviewKey = (p: any, i: number): string => {
    if (!p || typeof p === 'string') return `preview-${i}`;
    return p.id || `preview-${i}`;
  };

  const awaitAsyncPreviews = async (data: any) => {
    const ids: string[] =
      Array.isArray(data?.job_ids) && data.job_ids.length
        ? data.job_ids
        : data?.job_id
        ? [data.job_id]
        : [];
    if (!ids.length) throw new Error('Resposta async sem job_id');
    const jobs = await Promise.all(
      ids.map((id) =>
        waitForJob(id, {
          onStatus: (j) => setLoadingStep(jobStatusLabel(j)),
        }),
      ),
    );
    return {
      generation_id: data.generation_id as string | undefined,
      previews: jobs.map((j, i) => {
        const extras =
          j.input && typeof j.input === 'object' && !Array.isArray(j.input)
            ? (j.input as { extras?: { preview_label?: string } }).extras
            : undefined;
        return {
          id: j.id,
          label: extras?.preview_label || `Opção ${i + 1}`,
          image_b64: toMediaUrl(j.result_url),
          prompt: '',
        };
      }),
    };
  };

  const awaitAsyncVideo = async (data: any, label = 'Vídeo') => {
    if (!data?.job_id) throw new Error('Resposta async sem job_id');
    trackBackgroundVideoJob(data.job_id, label);
    // Non-blocking: form stays usable; progress bar tracks the job.
    return null;
  };

  // Gerar 3 previews (Frame Final) via I2I
  const handleGeneratePreviews = async () => {
    if (!stagingBaseB64 || !decorPrompt) {
      setError("Envie a foto do espaço e descreva a decoração desejada.");
      return;
    }
    setLoading(true);
    setLoadingStep('Gerando 3 propostas via IA...');
    setError(null);
    setPreviews([]);
    setFinalVideo(null);
    setGenerationId(null);
    setSelectedPreview(null);

    try {
      const orgId = await getUserOrganizationId();
      if (!orgId) throw new Error("Você não está em nenhuma organização.");

      const { data, error } = await supabase.functions.invoke('create-preview', {
        body: { 
          image_b64: stagingBaseB64, 
          prompt: decorPrompt, 
          organization_id: orgId,
          aspect_ratio: aspectRatio,
          image_model_id: imageModelId,
        }
      });
      if (error) {
        if (error.message?.includes('Not enough credits')) {
          const isFinOpsOrAdmin = userRole === 'admin' || userRole === 'fin_ops';
          const msg = isFinOpsOrAdmin 
            ? "Saldo insuficiente na organização para gerar previews. Como gestor, adicione créditos no menu Pagamentos."
            : "Sua organização está sem créditos para previews. Por favor, abra uma solicitação para a equipe financeira.";
          throw new Error(msg);
        }
        throw new Error(error.message);
      }

      if (data?.async && (data.job_id || data.job_ids)) {
        setLoadingStep('Gerando propostas...');
        const resolved = await awaitAsyncPreviews(data);
        setPreviews(resolved.previews);
        setGenerationId(resolved.generation_id || null);
      } else if (data?.success) {
        console.log('Preview response:', JSON.stringify(data.previews?.map((p: any) => ({ type: typeof p, keys: typeof p === 'object' ? Object.keys(p) : null, hasB64: typeof p === 'object' ? !!p.image_b64 : null }))));
        // Normalize previews: handle both string[] and object[] formats
        const normalized = (data.previews || []).map((p: any, i: number) => {
          if (typeof p === 'string') {
            return { id: `preview-${i}`, label: `Opção ${i + 1}`, image_b64: p, prompt: '' };
          }
          return p;
        });
        setPreviews(normalized);
        setGenerationId(data.generation_id);
      } else if (data?.error) {
        if (data.error.includes('Not enough credits')) {
          const isFinOpsOrAdmin = userRole === 'admin' || userRole === 'fin_ops';
          const msg = isFinOpsOrAdmin 
            ? "Saldo insuficiente na organização para gerar previews. Como gestor, adicione créditos no menu Pagamentos."
            : "Sua organização está sem créditos para previews. Por favor, abra uma solicitação para a equipe financeira.";
          throw new Error(msg);
        }
        throw new Error(data.error);
      }
    } catch (err: any) {
      setError(err?.message || "Erro na geração de imagens.");
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  // Seleciona preview e define como end frame
  const handleSelectPreview = (index: number) => {
    setSelectedPreview(index);
    const preview = previews[index];
    const src = getPreviewImgSrc(preview);
    if (!src) return;
    setStagingRefs((prev) => {
      if (stagingMultiRef) {
        if (prev.length >= stagingMaxRefs) return prev;
        const hasEnd = prev.some((r) => r.role === 'end');
        return [...prev, { id: newRefId(), image_b64: src, role: hasEnd ? null : 'end', label: getPreviewLabel(preview, index) }];
      }
      const withoutEnd = prev.filter((r) => r.role !== 'end');
      return [...withoutEnd, { id: newRefId(), image_b64: src, role: 'end' }];
    });
    setEndFrameB64(src);
  };

  // Helper to size canvas
  const imgRef = useRef<HTMLImageElement | null>(null);

  const syncCanvasSize = () => {
    if (imgRef.current && droneCanvasRef.current) {
      droneCanvasRef.current.width = imgRef.current.clientWidth;
      droneCanvasRef.current.height = imgRef.current.clientHeight;
    }
  };

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>): Point2D | null => {
    if (!droneCanvasRef.current) return null;
    const rect = droneCanvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // Canvas drawing handlers
  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e);
    if (!coords) return;

    if (droneDrawMode === 'approach') {
      setDroneApproachPoint(coords);
      setDroneDrawMode('path');
      return;
    }

    // Continuous path only: new stroke replaces the previous trajectory.
    setIsDrawing(true);
    setDronePoints([coords]);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || droneDrawMode !== 'path') return;
    const coords = getCanvasCoords(e);
    if (!coords) return;
    setDronePoints(prev => [...prev, coords]);
  };

  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
  };

  const getCanvasSize = () => ({
    width: droneCanvasRef.current?.width || 1,
    height: droneCanvasRef.current?.height || 1,
  });

  // Redraw path in UI canvas
  useEffect(() => {
    if (!droneCanvasRef.current) return;
    const canvas = droneCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawScenesOverlay(ctx, [], dronePoints, droneApproachPoint);
  }, [dronePoints, droneApproachPoint]);

  const getFlightPathImage = (): Promise<string> => {
    return new Promise((resolve) => {
      if (!droneCanvasRef.current || !startFrameB64) {
        resolve(startFrameB64);
        return;
      }
      const canvas = droneCanvasRef.current;
      const img = new Image();
      img.onload = () => {
        const mergeCanvas = document.createElement('canvas');
        mergeCanvas.width = img.naturalWidth;
        mergeCanvas.height = img.naturalHeight;
        const ctx = mergeCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const displaySize = { width: canvas.width, height: canvas.height };
          // Same stroke the user drew (no RDP simplify) — only drop near-duplicate points.
          // No S/E markers or arrows in the API image so the model does not paint HUD chrome.
          const stroke = densifyStrokeForExport(dronePoints, 2);
          if (stroke.length >= 2) {
            drawPathOnImage(ctx, stroke, droneApproachPoint, displaySize, {
              showMarkers: false,
              showArrows: false,
            });
          }
        }
        resolve(mergeCanvas.toDataURL('image/jpeg', 0.92));
      };
      img.src = startFrameB64;
    });
  };

  const buildScenesWithAnchors = async (): Promise<FlightScene[]> => {
    const canvasSize = getCanvasSize();
    const base = buildFlightScenes(
      [],
      canvasSize,
      duration,
      droneApproachPoint,
      dronePoints,
    );
    if (base.length === 0 || !startFrameB64) return base;

    const withAnchors: FlightScene[] = [];
    for (let i = 0; i < base.length; i++) {
      const scene = base[i];
      try {
        const anchors = await buildPathAnchorFrames(
          startFrameB64,
          scene.points,
          canvasSize,
          duration,
          i === 0 ? droneApproachPoint : null,
        );
        if (anchors) {
          withAnchors.push({
            ...scene,
            direction: anchors.direction,
            speed: anchors.speed,
            startNorm: anchors.startNorm,
            endNorm: anchors.endNorm,
            bearing: anchors.bearing,
            lengthPx: anchors.lengthPx,
            start_frame_b64: anchors.startB64,
            end_frame_b64: anchors.endB64,
          });
        } else {
          withAnchors.push(scene);
        }
      } catch {
        withAnchors.push(scene);
      }
    }
    // Renormalize percentages after any drops
    const total = withAnchors.reduce((s, e) => s + Math.max(e.lengthPx, 1), 0) || 1;
    return withAnchors.map((s) => ({
      ...s,
      percentage: Math.max(0.01, s.lengthPx / total),
    }));
  };

  // Gerar vídeo Kling V3
  const handleGenerateVideo = async () => {
    let animateFinish = false;
    
    if (creationMode === 'drone' && !startFrameB64) {
      setError("Suba uma imagem inicial do prédio antes de gerar o plano de voo.");
      return;
    }

    const hasPath = dronePoints.length >= 2;

    if (creationMode === 'drone' && !hasPath) {
      setError('Desenhe o percurso do drone sobre a imagem (mínimo 2 pontos). Marque S=início e E=fim.');
      return;
    }

    if (creationMode === 'staging' && storyboard.length === 0) {
      await analyzeTagsFromPrompt(videoPrompt);
      return;
    }

    setLoading(true);
    setLoadingStep('Preparando...');
    try {
      if (creationMode === 'staging') {
        void persistStagingHistory();
      }
      const orgId = await getUserOrganizationId();
      if (!orgId) throw new Error("Você não está em nenhuma organização.");

      let currentStoryboard = storyboard;
      let finalSceneConfig = null;

      if (creationMode === 'drone') {
        setLoadingStep('Preparando o plano de voo...');
        const pathImage = await getFlightPathImage();
        const flightScenes = await buildScenesWithAnchors();
        if (flightScenes.length === 0) {
          throw new Error('Não foi possível montar as cenas de voo. Desenhe novamente o trajeto.');
        }

        const { data: droneConfig, error: configError } = await supabase.functions.invoke('generate-video-config', {
          body: {
            prompt_text: "Realistic first-person flying camera, cinematic real estate showcase, natural lighting",
            start_frame: startFrameB64,
            flight_path_image: pathImage,
            drone_references: droneReferences.map(r => ({ image_b64: r.image_b64, label: r.label, role: r.role || 'ref' })),
            reference_images: [
              { image_b64: startFrameB64, role: 'start', label: 'Frame do traçado' },
              ...droneReferences.map((r, i) => ({
                image_b64: r.image_b64,
                role: r.role === 'end' ? 'end' : 'ref',
                label: r.label || `Ângulo ${i + 1}`,
              })),
            ],
            aspect_ratio: aspectRatio,
            is_drone: true,
            duration: duration,
            flight_segments: flightScenes.map((s) => ({
              direction: s.direction,
              percentage: s.percentage,
              speed: s.speed,
              startNorm: s.startNorm,
              endNorm: s.endNorm,
              lengthPx: s.lengthPx,
            })),
            flight_path: {
              waypoints: flightScenes.flatMap((s) => [
                { x: s.startNorm.x, y: s.startNorm.y },
                { x: s.endNorm.x, y: s.endNorm.y },
              ]),
              segments: flightScenes.map((s) => ({
                direction: s.direction,
                percentage: s.percentage,
                speed: s.speed,
                startNorm: s.startNorm,
                endNorm: s.endNorm,
              })),
              has_approach: !!droneApproachPoint,
              total_length_px: flightScenes.reduce((a, s) => a + s.lengthPx, 0),
              scene_count: flightScenes.length,
            },
          }
        });

        if (configError) throw new Error(`IA falhou ao processar a trajetória do drone. Detalhes: ${configError.message || JSON.stringify(configError)}`);
        if (!droneConfig) throw new Error("IA falhou ao processar a trajetória do drone. O retorno da IA está vazio.");
        finalSceneConfig = droneConfig;

        currentStoryboard = flightScenes.map((s, idx) => ({
          label: flightScenes.length === 1 ? 'Voo contínuo' : `Cena ${idx + 1} — ${s.direction}`,
          description: `${s.bearing}: (${Math.round(s.startNorm.x * 100)}%,${Math.round(s.startNorm.y * 100)}%) → (${Math.round(s.endNorm.x * 100)}%,${Math.round(s.endNorm.y * 100)}%)`,
          prompt_en: droneConfig.camera_directives || 'First-person continuous flight',
        }));
        setStoryboard(currentStoryboard);

        setLoadingStep('Gerando o vídeo...');
        const droneTimedWaypoints = sampleTimedWaypoints(
          flightScenes.length === 1 ? flightScenes[0].points : flightScenes.flatMap((s) => s.points),
          getCanvasSize(),
          duration,
          droneApproachPoint,
          20,
          { preserveStroke: true },
        );

        const anchorStart = flightScenes[0].start_frame_b64 || startFrameB64;
        const anchorEnd = flightScenes[flightScenes.length - 1].end_frame_b64 || '';

        const { data, error } = await supabase.functions.invoke('create-video', {
          body: {
            generation_id: generationId,
            selected_index: selectedPreview !== null ? selectedPreview : undefined,
            organization_id: orgId,
            duration_seconds: duration,
            scene_config: finalSceneConfig,
            tags: [],
            storyboard: currentStoryboard,
            start_frame_b64: anchorStart,
            end_frame_b64: anchorEnd,
            aspect_ratio: aspectRatio,
            drone_references: droneReferences.map(r => ({ image_b64: r.image_b64, label: r.label, role: r.role || 'ref' })),
            flight_path_image: pathImage,
            ...videoModelBody(droneVideoPick, 'flight_plan'),
            flight_scenes: flightScenes.map((s, idx) => ({
              index: idx + 1,
              direction: s.direction,
              speed: s.speed,
              percentage: s.percentage,
              bearing: s.bearing,
              startNorm: s.startNorm,
              endNorm: s.endNorm,
              lengthPx: s.lengthPx,
              start_frame_b64: s.start_frame_b64,
              end_frame_b64: s.end_frame_b64,
            })),
            flight_waypoints: droneTimedWaypoints,
          }
        });

        if (error) {
          if (error.message?.includes('Not enough credits')) {
            const isFinOpsOrAdmin = userRole === 'admin' || userRole === 'fin_ops';
            const msg = isFinOpsOrAdmin
              ? "Saldo insuficiente na organização. Como gestor, você pode adicionar créditos no menu Pagamentos."
              : "Sua organização está sem créditos. Por favor, abra uma solicitação para a equipe financeira.";
            throw new Error(msg);
          }
          throw new Error(error.message);
        }

        if (data?.dry_run) {
          setLoadingStep('Teste concluído — nada foi gerado nem debitado.');
          setLoading(false);
          setRenderingVideo(false);
          return;
        }

        if (data?.async && data.job_id) {
          setLoadingStep('Na fila — acompanhe o progresso');
          await awaitAsyncVideo(data, 'Plano de voo');
        } else if (data?.success && data.task_id) {
          const taskId = data.task_id;
          setLoadingStep('Gerando o vídeo...');
          setRenderingVideo(true);

          const maxAttempts = 60;
          let attempts = 0;

          const poll = async (): Promise<void> => {
            attempts++;
            if (attempts > maxAttempts) {
              throw new Error('Tempo limite de renderização excedido. Tente novamente mais tarde.');
            }

            const { data: pollData, error: pollError } = await supabase.functions.invoke('create-video', {
              body: {
                task_id: taskId,
                generation_id: generationId,
                organization_id: orgId
              }
            });

            if (pollError) throw new Error(pollError.message);
            if (!pollData?.success) throw new Error(pollData?.error || 'Erro ao verificar status do vídeo.');

            if (pollData.status === 'completed' && pollData.final_video) {
              setPendingVideoUrl(pollData.final_video);
              setFastForwardActive(true);
              animateFinish = true;
              return;
            }

            if (pollData.status === 'submitted' || pollData.status === 'processing') {
              const statusMsg = pollData.status === 'processing'
                ? `Gerando o vídeo... (${attempts}/${maxAttempts})`
                : `Na fila — acompanhe o progresso (${attempts}/${maxAttempts})`;
              setLoadingStep(statusMsg);
              await new Promise(r => setTimeout(r, 5000));
              return poll();
            }

            throw new Error(pollData?.error || `Status inesperado: ${pollData.status}`);
          };

          await poll();
        } else {
          throw new Error(data?.error || 'Falha ao iniciar geração do vídeo.');
        }
      } else {
        setLoadingStep('Preparando a cena...');
        const activeTagsData = tags.filter(t => t.active);
        const activeLabels = activeTagsData.map(t => t.label);
        const tagPrompts = activeTagsData.map(t => ({ id: t.id, label: t.label, prompt_json: t.prompt_json }));
        
        const { data: sceneConfig, error: configError } = await supabase.functions.invoke('generate-video-config', {
          body: { 
            prompt_text: videoPrompt || decorPrompt, 
            tags: activeLabels,
            tag_prompts: tagPrompts,
            storyboard,
            start_frame: stagingBaseB64 || startFrameB64,
            end_frame: stagingEndB64 || endFrameB64,
            reference_images: stagingReferencePayload,
            aspect_ratio: aspectRatio
          }
        });

        if (configError) throw new Error("IA falhou ao gerar configuração de cena.");
        finalSceneConfig = sceneConfig;

      setLoadingStep('Gerando o vídeo...');
      const { data, error } = await supabase.functions.invoke('create-video', {
        body: {
          generation_id: generationId,
          selected_index: selectedPreview !== null ? selectedPreview : undefined,
          organization_id: orgId,
          duration_seconds: duration,
          scene_config: finalSceneConfig,
          tags: tags.filter(t => t.active).map(t => t.id),
          storyboard: currentStoryboard,
          end_frame_b64: stagingEndB64 || endFrameB64,
          start_frame_b64: stagingBaseB64 || startFrameB64 || undefined,
          reference_images: stagingReferencePayload,
          aspect_ratio: aspectRatio,
          ...videoModelBody(stagingVideoPick, 'staging'),
        }
      });

      if (error) {
        if (error.message?.includes('Not enough credits')) {
          const isFinOpsOrAdmin = userRole === 'admin' || userRole === 'fin_ops';
          const msg = isFinOpsOrAdmin 
            ? "Saldo insuficiente na organização. Como gestor, você pode adicionar créditos no menu Pagamentos."
            : "Sua organização está sem créditos. Por favor, abra uma solicitação para a equipe financeira.";
          throw new Error(msg);
        }
        throw new Error(error.message);
      }

      if (data?.dry_run) {
        setLoadingStep('Teste concluído — nada foi gerado nem debitado.');
        setLoading(false);
        setRenderingVideo(false);
        return;
      }

      if (data?.async && data.job_id) {
        setLoadingStep('Na fila — acompanhe o progresso');
        await awaitAsyncVideo(data, 'Staging');
      } else if (data?.success && data.task_id) {
        const taskId = data.task_id;
        setLoadingStep('Gerando o vídeo...');
        setRenderingVideo(true);
        
        const maxAttempts = 60;
        let attempts = 0;
        
        const poll = async (): Promise<void> => {
          attempts++;
          if (attempts > maxAttempts) {
            throw new Error('Tempo limite de renderização excedido. Tente novamente mais tarde.');
          }
          
          const { data: pollData, error: pollError } = await supabase.functions.invoke('create-video', {
            body: {
              task_id: taskId,
              generation_id: generationId,
              organization_id: orgId
            }
          });
          
          if (pollError) throw new Error(pollError.message);
          if (!pollData?.success) throw new Error(pollData?.error || 'Erro ao verificar status do vídeo.');
          
          if (pollData.status === 'completed' && pollData.final_video) {
            setPendingVideoUrl(pollData.final_video);
            setFastForwardActive(true);
            animateFinish = true;
            return;
          }
          
          if (pollData.status === 'submitted' || pollData.status === 'processing') {
            const statusMsg = pollData.status === 'processing' 
              ? `Gerando o vídeo... (${attempts}/${maxAttempts})`
              : `Na fila — acompanhe o progresso (${attempts}/${maxAttempts})`;
            setLoadingStep(statusMsg);
            await new Promise(r => setTimeout(r, 5000));
            return poll();
          }
          
          throw new Error(pollData?.error || `Status inesperado: ${pollData.status}`);
        };
        
        await poll();
      } else {
        throw new Error(data?.error || 'Falha ao iniciar geração do vídeo.');
      }
      }
    } catch (err: any) {
      setError(err?.message || "Erro ao gerar vídeo.");
      setRenderingVideo(false);
    } finally {
      if (!animateFinish) {
        setLoading(false);
        setLoadingStep('');
        setRenderingVideo(false);
      }
    }
  };

  const resetAll = () => {
    setFinalVideo(null);
    setJobDeliveredToGallery(false);
    setPreviews([]);
    setStartFrameB64('');
    setEndFrameB64('');
    setStagingRefs([]);
    setDecorPrompt('');
    setVideoPrompt('');
    setGenerationId(null);
    setSelectedPreview(null);
    setTags(prev => prev.map(t => ({ ...t, active: false })));
    setStoryboard([]);
    setError(null);
    setDronePoints([]);
    setDroneApproachPoint(null);
    setDroneDrawMode('path');
    setDroneReferences([]);
  };

  const removeDroneRef = (id: string) => {
    setDroneReferences(prev => prev.filter(r => r.id !== id));
  };

  const updateDroneRefLabel = (id: string, label: string) => {
    setDroneReferences(prev => prev.map(r => r.id === id ? { ...r, label } : r));
  };

  // Persist hidden tags and collapsed categories
  useEffect(() => {
    localStorage.setItem('hidden_tags', JSON.stringify(Array.from(hiddenTagIds)));
  }, [hiddenTagIds]);
  useEffect(() => {
    localStorage.setItem('collapsed_tag_cats', JSON.stringify(Array.from(collapsedCategories)));
  }, [collapsedCategories]);

  const hideTag = (id: string) => {
    setHiddenTagIds(prev => {
      const next = new Set(Array.from(prev));
      next.add(id);
      return next;
    });
  };

  const restoreAllTags = () => {
    setHiddenTagIds(new Set());
  };

  const toggleCategory = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(Array.from(prev));
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  // Group VISIBLE tags by category
  const visibleTags = tags.filter(t => !hiddenTagIds.has(t.id));
  const tagsByCategory = visibleTags.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {} as Record<string, SceneTag[]>);
  const hiddenCount = hiddenTagIds.size;

  // Derived variables moved to component top to avoid hoisting issues

  // ======= SHARED STYLES =======
  const cardStyle: React.CSSProperties = {
    background: 'rgba(25,27,33,0.7)', 
    backdropFilter: 'blur(10px)',
    borderRadius: '18px', 
    border: '1px solid rgba(255,255,255,0.08)', 
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    minWidth: 0,
  };

  const cardHeaderStyle: React.CSSProperties = {
    padding: '0.7rem 1rem', 
    borderBottom: '1px solid rgba(255,255,255,0.05)', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '0.5rem', 
    fontSize: '0.78rem', 
    fontWeight: 600, 
    color: 'var(--text-muted)',
  };

  const inputBarStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'flex-end', gap: '0.5rem',
    background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '0.4rem 0.6rem',
    border: '1px solid rgba(255,255,255,0.08)', transition: 'border-color 0.2s',
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1, background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', resize: 'none', fontFamily: 'inherit', fontSize: '0.85rem',
    padding: '5px 0', maxHeight: '80px', lineHeight: '1.4',
  };

  const sendBtnStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: '9px', border: 'none',
    background: active ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : 'var(--text-muted)',
    cursor: active ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s', flexShrink: 0,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', maxWidth: '100%', minHeight: '100%' }}>
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        /* Professional scrollbar for the main container */
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
        
        @media (max-width: 768px) {
          .main-scroll-container { padding: 0.75rem !important; }
          .frames-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      
      {/* ====== MODE TABS ====== */}
      <div style={{
        display: 'flex', gap: '2px', padding: '0.75rem 1.25rem 0',
        background: 'transparent', flexShrink: 0, overflowX: 'auto'
      }}>

        <button
          onClick={() => setCreationMode('staging')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.55rem 1.2rem', borderRadius: '10px 10px 0 0',
            border: 'none', fontSize: '0.78rem', fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.2s',
            background: creationMode === 'staging' ? 'rgba(25,27,33,0.7)' : 'rgba(255,255,255,0.03)',
            color: creationMode === 'staging' ? '#818cf8' : 'var(--text-muted)',
            borderBottom: creationMode === 'staging' ? '2px solid #6366f1' : '2px solid transparent',
          }}
        >
          <Sparkles size={14} /> Staging Rápido
        </button>
        <button
          onClick={() => setCreationMode('drone')}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.55rem 1.2rem', borderRadius: '10px 10px 0 0',
            border: 'none', fontSize: '0.78rem', fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer', transition: 'all 0.2s',
            background: creationMode === 'drone' ? 'rgba(25,27,33,0.7)' : 'rgba(255,255,255,0.03)',
            color: creationMode === 'drone' ? '#06b6d4' : 'var(--text-muted)',
            borderBottom: creationMode === 'drone' ? '2px solid #06b6d4' : '2px solid transparent',
          }}
        >
          <Camera size={14} /> Plano de Voo (Drone POV)
        </button>
      </div>

      <>
      {/* ====== SCROLLABLE CONTENT ====== */}
      <div className="main-scroll-container" style={{ flex: 1, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* Slim progress bar — does not block new creations */}
        {bgVideoJobs.length > 0 && (
          <div
            style={{
              width: '100%',
              padding: '0.55rem 0.75rem',
              borderRadius: 10,
              border: '1px solid rgba(99,102,241,0.22)',
              background: 'rgba(99,102,241,0.06)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {bgVideoJobs.map((job) => {
              const pct = statusProgress(job.status);
              const failed = job.status === 'failed';
              return (
                <div key={job.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: '0.7rem', color: failed ? '#f87171' : 'rgba(255,255,255,0.7)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.label} · {job.step}
                    </span>
                    <button
                      type="button"
                      onClick={() => dismissBgJob(job.id)}
                      title="Ocultar da barra (o job continua na nuvem)"
                      style={{
                        flexShrink: 0,
                        background: 'transparent',
                        border: 'none',
                        color: 'rgba(255,255,255,0.35)',
                        cursor: 'pointer',
                        fontSize: '0.65rem',
                        padding: '0 2px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <div style={{ width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: failed ? '100%' : `${pct}%`,
                        height: '100%',
                        borderRadius: 2,
                        background: failed
                          ? '#ef4444'
                          : 'linear-gradient(90deg, #6366f1, #a855f7)',
                        transition: 'width 0.45s ease-out',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* ====== DRONE FLIGHT PLANNER MODE UI ====== */}
        {creationMode === 'drone' && !finalVideo && (
          <>
            <div className="frames-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: '1.15rem' }}>
              {/* FRAME INICIAL E TRAÇADO */}
              <div style={cardStyle}>
                <div style={{ ...cardHeaderStyle, color: '#06b6d4' }}>
                  <Camera size={16} style={{ color: '#06b6d4' }} /> Frame Inicial — Traçado do Drone
                </div>
                <div style={{ padding: '0.65rem 0.75rem 0', fontSize: '0.68rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: 'var(--text)' }}>Linha contínua:</strong>{' '}
                    arraste um único percurso S→E (pode ziguezaguear). Um take sem cortes; a imagem com a linha vermelha vai como referência de trajetória para o modelo (a linha não aparece no vídeo final).
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#22d3ee' }}>Linha azul (Ponto de Aproximação):</strong> opcional. Clique em &quot;Ponto de Aproximação&quot; e depois toque na imagem para marcar o ponto <strong>A</strong>.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem', paddingTop: '0.15rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> S = início
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /> E = fim
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ width: 16, height: 3, borderRadius: 2, background: '#ef4444', display: 'inline-block' }} /> percurso do voo
                    </span>
                  </div>
                </div>
                <div style={{ padding: '1.1rem', minHeight: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  {!startFrameB64 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem 1.25rem', color: 'var(--text-muted)', width: '100%' }}>
                      <UploadCloud size={40} style={{ opacity: 0.35 }} />
                      <span style={{ fontSize: '0.92rem', textAlign: 'center', lineHeight: 1.4 }}>Foto do prédio — upload ou galeria</span>
                      <MediaSourceButtons
                        accept="image"
                        accent="#06b6d4"
                        onImageSelected={(dataUrl) => applyImageToFrame(dataUrl, setStartFrameB64)}
                      />
                    </div>
                  ) : (
                    <div style={{ position: 'relative', display: 'inline-block', width: '100%', textAlign: 'center' }}>
                      <img 
                        ref={imgRef}
                        src={startFrameB64} 
                        alt="Prédio Base" 
                        onLoad={syncCanvasSize}
                        style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', borderRadius: '10px', background: 'rgba(0,0,0,0.2)', display: 'block', margin: '0 auto' }} 
                      />
                      <canvas
                        ref={droneCanvasRef}
                        onMouseDown={handleCanvasMouseDown}
                        onMouseMove={handleCanvasMouseMove}
                        onMouseUp={handleCanvasMouseUp}
                        onMouseLeave={handleCanvasMouseUp}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          cursor: droneDrawMode === 'approach' ? 'cell' : 'crosshair',
                          borderRadius: '10px',
                          zIndex: 10
                        }}
                      />
                      <button 
                        onClick={() => { setStartFrameB64(''); setDronePoints([]); setDroneApproachPoint(null); setDroneDrawMode('path'); }} 
                        style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.65)', border: 'none', color: '#fff', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12 }}
                      >
                        <X size={12} />
                      </button>
                      <div style={{ position: 'absolute', bottom: 10, left: 10, display: 'flex', gap: '6px', zIndex: 12, flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => { setDronePoints([]); }}
                          style={{
                            background: 'rgba(0,0,0,0.7)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#ef4444',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            padding: '0.3rem 0.6rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <RotateCcw size={11} /> Limpar Trajeto
                        </button>
                        <button
                          onClick={() => setDroneDrawMode('approach')}
                          style={{
                            background: droneDrawMode === 'approach' ? 'rgba(34,211,238,0.25)' : 'rgba(0,0,0,0.7)',
                            border: droneDrawMode === 'approach' ? '1px solid #22d3ee' : '1px solid rgba(255,255,255,0.1)',
                            color: '#22d3ee',
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            padding: '0.3rem 0.6rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Move size={11} /> Ponto de Aproximação
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* REFERÊNCIAS EXTRAS */}
              <div style={cardStyle}>
                <div style={{ ...cardHeaderStyle, color: '#06b6d4' }}>
                  <ImageIcon size={15} style={{ color: '#06b6d4' }} /> Referências de Outros Ângulos {droneMultiRef ? `(Opcional — ${droneVideoModel.label}, até ${droneMaxExtras})` : '(Opcional)'}
                </div>
                <ImageDropZone
                  disabled={droneReferences.length >= droneMaxExtras || droneUploading}
                  onFiles={ingestDroneFiles}
                  hint="Solte as imagens para adicionar"
                  style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
                >
                  {droneReferences.length < droneMaxExtras && (
                    <div style={{
                      border: '1.5px dashed rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      padding: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.55rem',
                      background: 'rgba(255,255,255,0.01)',
                      color: 'var(--text-muted)',
                    }}>
                      <UploadCloud size={20} style={{ opacity: 0.4 }} />
                      <span style={{ fontSize: '0.72rem', fontWeight: 500 }}>Adicionar referência</span>
                      <span style={{ fontSize: '0.65rem', opacity: 0.75, textAlign: 'center' }}>
                        Arraste várias fotos, galeria ou upload
                      </span>
                      <MediaSourceButtons
                        accept="image"
                        accent="#06b6d4"
                        compact
                        multiple
                        ingestFiles={ingestDroneFiles}
                        uploading={droneUploading}
                        onImageSelected={(dataUrl, meta) => applyDroneReferenceImage(dataUrl, meta?.title)}
                      />
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '240px', overflowY: 'auto' }}>
                    {droneReferences.map((ref) => (
                      <div key={ref.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.4rem', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: ref.role === 'end' ? '1px solid rgba(168,85,247,0.45)' : '1px solid rgba(255,255,255,0.05)' }}>
                        <img src={ref.image_b64} alt="Referência" style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px', background: 'rgba(0,0,0,0.35)' }} />
                        <div style={{ flex: 1 }}>
                          <input 
                            type="text" 
                            placeholder="Descreva esta referência (Ex: visão do topo)..."
                            value={ref.label}
                            onChange={(e) => updateDroneRefLabel(ref.id, e.target.value)}
                            style={{
                              width: '100%',
                              background: 'rgba(0,0,0,0.25)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: '6px',
                              padding: '0.3rem 0.5rem',
                              color: '#fff',
                              fontSize: '0.72rem',
                              outline: 'none'
                            }}
                          />
                        </div>
                        {droneMultiRef && (
                          <button
                            type="button"
                            onClick={() => setDroneReferences((prev) => prev.map((r) => ({
                              ...r,
                              role: r.id === ref.id ? (r.role === 'end' ? null : 'end') : (r.role === 'end' ? null : r.role),
                            })))}
                            style={{
                              background: ref.role === 'end' ? 'rgba(168,85,247,0.2)' : 'none',
                              border: `1px solid ${ref.role === 'end' ? '#a855f7' : 'rgba(255,255,255,0.12)'}`,
                              color: ref.role === 'end' ? '#d8b4fe' : 'rgba(255,255,255,0.5)',
                              cursor: 'pointer',
                              padding: '0.2rem 0.4rem',
                              borderRadius: 6,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              fontFamily: 'inherit',
                            }}
                          >
                            Final
                          </button>
                        )}
                        <button 
                          onClick={() => removeDroneRef(ref.id)}
                          style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', padding: '0.2rem' }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </ImageDropZone>
              </div>
            </div>

            {/* CONFIGURAÇÃO DO TRAJETO E GERAÇÃO */}
            {startFrameB64 && (
              <div style={{ ...cardStyle, border: '1px solid rgba(6,182,212,0.2)' }}>
                <div style={{ ...cardHeaderStyle, background: 'rgba(6,182,212,0.06)', color: '#22d3ee' }}>
                  <Video size={15} /> Configuração do Vídeo de Drone
                </div>
                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    A câmera seguirá o percurso desenhado em primeira pessoa (POV), com estilo cinematográfico realista automático. Escolha a duração e gere o vídeo.
                    {duration <= 5 && (
                      <span style={{ display: 'block', marginTop: '0.35rem', color: '#fbbf24' }}>
                        Dica: rotas complexas (zigzag / várias curvas) seguem bem melhor em <strong>10s</strong> — em 5s o sistema limita a 5 trechos e cada um fica muito curto.
                      </span>
                    )}
                  </div>
                  <VideoModelPicker
                    modality="i2v"
                    value={{ ...droneVideoPick, durationSeconds: duration }}
                    onChange={(next) => {
                      setDroneVideoPick(next);
                      setDuration(next.durationSeconds);
                    }}
                    durationSeconds={duration}
                    onDurationChange={(s) => {
                      setDuration(s);
                      setDroneVideoPick((p) => ({ ...p, durationSeconds: s }));
                    }}
                    recommendedModelId={droneModelUseCount > 0 ? droneRecommendedModelId : null}
                    compact
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button
                      onClick={handleGenerateVideo}
                      disabled={loading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
                        background: loading ? 'rgba(6,182,212,0.35)' : '#06b6d4',
                        color: '#fff', fontSize: '0.78rem', fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit'
                      }}
                    >
                      <Sparkles size={14} /> Gerar Vídeo
                    </button>
                  </div>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                    ~{droneCreditsEstimate} créditos
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ====== STAGING UI ====== */}
        {creationMode === 'staging' && !finalVideo && (
          <>
            {stagingMultiRef ? (
              <div style={cardStyle}>
                <div style={{ ...cardHeaderStyle, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    <ImageIcon size={16} style={{ color: '#6366f1' }} /> Referências do espaço
                  </span>
                  <VideoModelPicker
                    variant="header"
                    modality="i2v"
                    value={{ ...stagingVideoPick, durationSeconds: duration }}
                    onChange={applyStagingVideoPick}
                    durationSeconds={duration}
                    onDurationChange={(s) => {
                      setDuration(s);
                      setStagingVideoPick((p) => ({ ...p, durationSeconds: s }));
                    }}
                    recommendedModelId={stagingModelUseCount > 0 ? stagingRecommendedModelId : null}
                  />
                </div>
                <ReferenceImageTray
                  images={stagingRefs}
                  max={stagingMaxRefs}
                  accent="#6366f1"
                  modelLabel={stagingVideoModel.label}
                  emptyHint={`Envie fotos do espaço. ${stagingVideoModel.label} aceita até ${stagingMaxRefs} referências. Com 3+ imagens, a ordem da bandeja é a sequência cronológica (estágios). Início e fim são opcionais. Cinemático e Ultra Realista são exclusivos.`}
                  onAdd={(dataUrl, meta) => addStagingImage(dataUrl, meta?.title)}
                  onRemove={removeStagingImage}
                  onSetRole={setStagingRole}
                  onTransform={(id) => openTransformModal(id)}
                />
              </div>
            ) : (
            <div className="frames-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: '1.15rem' }}>

          
          {/* FRAME INICIAL */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <Camera size={16} style={{ color: '#6366f1' }} /> Frame Inicial — Espaço Vazio
            </div>
            <div style={{ padding: '1.1rem', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {!stagingBaseB64 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem 1.25rem', color: 'var(--text-muted)', width: '100%' }}>
                  <UploadCloud size={40} style={{ opacity: 0.35 }} />
                  <span style={{ fontSize: '0.92rem', textAlign: 'center', lineHeight: 1.4 }}>Foto do espaço — upload ou galeria</span>
                  <MediaSourceButtons
                    accept="image"
                    accent="#6366f1"
                    onImageSelected={(dataUrl) => addStagingImage(dataUrl)}
                  />
                </div>
              ) : (
                <>
                  <img src={stagingBaseB64} alt="Frame Inicial" style={{ width: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '10px', background: 'rgba(0,0,0,0.2)' }} />
                  <button onClick={resetAll} style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.65)', border: 'none', color: '#fff', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={12} />
                  </button>
                  <button
                    onClick={() => openTransformModal('start')}
                    title="Transformar Imagem com IA"
                    aria-label="Transformar Imagem com IA"
                    style={{
                      position: 'absolute',
                      bottom: 15,
                      right: 15,
                      background: 'rgba(99,102,241,0.9)',
                      border: 'none',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <Sparkles size={14} {...({ alt: "transformar imagem com IA" } as any)} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* FRAME FINAL */}
          <div style={{ ...cardStyle, border: stagingEndB64 ? '1px solid rgba(168,85,247,0.35)' : cardStyle.border }}>
            <div style={{ ...cardHeaderStyle, color: stagingEndB64 ? '#a855f7' : 'var(--text-muted)' }}>
              <Sparkles size={16} style={{ color: '#a855f7' }} /> Frame Final — Espaço Decorado
              {stagingEndB64 && (
                <button onClick={() => { setStagingRefs((prev) => prev.filter((r) => r.role !== 'end')); setEndFrameB64(''); setSelectedPreview(null); setPreviews([]); setFinalVideo(null); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 0 }}>
                  <X size={14} />
                </button>
              )}
            </div>
            <div style={{ padding: '1.1rem', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              
              {/* Imagem final definida */}
              {stagingEndB64 && (selectedPreview !== null || previews.length === 0) ? (
                <div style={{ position: 'relative', width: '100%' }}>
                  <img src={stagingEndB64} alt="Frame Final" style={{ width: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '10px', background: 'rgba(0,0,0,0.2)' }} />
                  <button
                    onClick={() => openTransformModal('end')}
                    title="Transformar Imagem com IA"
                    aria-label="Transformar Imagem com IA"
                    style={{
                      position: 'absolute',
                      bottom: 10,
                      right: 10,
                      background: 'rgba(168,85,247,0.9)',
                      border: 'none',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <Sparkles size={14} {...({ alt: "transformar imagem com IA" } as any)} />
                  </button>
                </div>
              ) : (
                /* Grid de 3 propostas da IA */
                (previews.length > 0) ? (
                  <div style={{ width: '100%' }}>
                    <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem', textAlign: 'center' }}>Selecione a melhor proposta:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                      {previews.map((preview, i) => {
                        const imgSrc = getPreviewImgSrc(preview);
                        const label = getPreviewLabel(preview, i);
                        return (
                        <div
                          key={getPreviewKey(preview, i)}
                          onClick={() => handleSelectPreview(i)}
                          style={{
                            borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                            border: selectedPreview === i ? '2px solid var(--primary)' : '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 0.2s', position: 'relative'
                          }}
                        >
                          <img src={imgSrc} alt={label} style={{ width: '100%', height: '90px', objectFit: 'contain', display: 'block', background: 'rgba(0,0,0,0.2)' }} />
                        </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.75rem' }}>
                      <button 
                        onClick={handleGeneratePreviews}
                        disabled={loading}
                        style={{
                          background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)', color: '#d8b4fe',
                          padding: '0.4rem 1rem', borderRadius: '8px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Sparkles size={12} /> Regenerar
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Upload manual / galeria do frame final */
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '2.25rem 1.5rem', color: 'var(--text-muted)', borderRadius: '14px', border: '1.5px dashed rgba(255,255,255,0.12)', width: '100%', minHeight: '240px' }}>
                    <UploadCloud size={36} style={{ opacity: 0.4 }} />
                    <span style={{ fontSize: '0.9rem', textAlign: 'center', lineHeight: 1.45 }}>Enviar foto, anexar da galeria ou gere com IA ↓</span>
                    <MediaSourceButtons
                      accept="image"
                      accent="#a855f7"
                      onImageSelected={(dataUrl) => addStagingImage(dataUrl)}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
            )}

            {stagingMultiRef && previews.length > 0 && !finalVideo && (
              <div style={{ ...cardStyle, padding: '1rem' }}>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem', textAlign: 'center' }}>
                  Clique numa proposta para adicioná-la às referências
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.4rem' }}>
                  {previews.map((preview, i) => {
                    const imgSrc = getPreviewImgSrc(preview);
                    const label = getPreviewLabel(preview, i);
                    return (
                      <div
                        key={getPreviewKey(preview, i)}
                        onClick={() => handleSelectPreview(i)}
                        style={{
                          borderRadius: '8px', overflow: 'hidden', cursor: 'pointer',
                          border: selectedPreview === i ? '2px solid var(--primary)' : '1px solid rgba(255,255,255,0.08)',
                          transition: 'all 0.2s',
                        }}
                      >
                        <img src={imgSrc} alt={label} style={{ width: '100%', height: '90px', objectFit: 'contain', display: 'block', background: 'rgba(0,0,0,0.2)' }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

        {/* ═══════════════════════════════════════════════════════════════
            PROMPT 1: Decoração (gera previews via IA)
            Aparece quando: tem frame inicial E não tem frame final ainda
            ═══════════════════════════════════════════════════════════════ */}
        {stagingBaseB64 && !finalVideo && (stagingMultiRef ? stagingRefs.length < stagingMaxRefs : !stagingEndB64) && (
          <div style={{ ...cardStyle, padding: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
              <Sparkles size={14} style={{ color: '#a855f7' }} />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#a855f7' }}>Descreva a decoração desejada</span>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                A IA gera 3 propostas ({previewCredits} créditos)
              </span>
            </div>

            <div style={{
              marginBottom: '0.6rem',
              padding: '0.4rem 0.5rem',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.04)',
            }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>Modelo</div>
              <ImageModelPicker
                compact
                aria-label="Modelo de imagem"
                value={imageModelId}
                onChange={setImageModelId}
              />
            </div>

            <div style={inputBarStyle}>
              <textarea
                ref={decorTextareaRef}
                rows={1}
                value={decorPrompt}
                onChange={(e) => { setDecorPrompt(e.target.value); handleTextareaInput(decorTextareaRef); }}
                onKeyDown={(e) => { 
                  if (e.key === 'Enter' && !e.shiftKey) { 
                    e.preventDefault(); 
                    handleGeneratePreviews(); 
                  } 
                }}
                placeholder="Ex: Sala moderna com sofá cinza, mesa de madeira e iluminação quente..."
                style={textareaStyle}
              />
              <button
                onClick={handleGeneratePreviews}
                disabled={loading || !stagingBaseB64 || !decorPrompt}
                style={sendBtnStyle(!!stagingBaseB64 && !!decorPrompt && !loading)}
              >
                <Sparkles size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            PROMPT 2: Vídeo (tags + configuração de cena)
            Aparece quando: tem frame inicial definido E não tem vídeo final ainda
            ═══════════════════════════════════════════════════════════════ */}
        {stagingBaseB64 && !finalVideo && (
          <div style={{ ...cardStyle, border: '1px solid rgba(99,102,241,0.2)' }}>
            
            {/* Header */}
            <div style={{ ...cardHeaderStyle, background: 'rgba(99,102,241,0.06)', color: '#818cf8' }}>
              <Video size={15} /> Configurar Vídeo
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: 'auto', fontWeight: 400 }}>
                As tags são preenchidas automaticamente
              </span>
            </div>

            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              
              {/* Tags de cena */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {/* Header row: analyzing + hidden count + restore */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {analyzingTags && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', fontSize: '0.65rem', color: 'var(--primary)' }}>
                      <div style={{ width: 8, height: 8, border: '1.5px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                      Analisando...
                    </div>
                  )}
                  {hiddenCount > 0 && (
                    <button
                      onClick={restoreAllTags}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        padding: '2px 8px', borderRadius: '12px', fontSize: '0.6rem',
                        border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
                        color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'inherit',
                        marginLeft: 'auto',
                      }}
                    >
                      <RotateCcw size={9} /> {hiddenCount} oculta{hiddenCount > 1 ? 's' : ''}
                    </button>
                  )}
                </div>

                {/* Collapsible categories */}
                {Object.entries(tagsByCategory).map(([cat, catTags]) => {
                  const catInfo = TAG_CATEGORIES[cat];
                  const isCollapsed = collapsedCategories.has(cat);
                  const activeCount = catTags.filter(t => t.active).length;
                  return (
                    <div key={cat}>
                      {/* Category header — clickable to collapse */}
                      <button
                        onClick={() => toggleCategory(cat)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '2px 6px', border: 'none', background: 'none',
                          color: catInfo?.color || 'var(--text-muted)', cursor: 'pointer',
                          fontSize: '0.6rem', fontWeight: 600, fontFamily: 'inherit',
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          opacity: 0.8, transition: 'opacity 0.15s',
                        }}
                      >
                        {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
                        {catInfo?.label || cat}
                        {isCollapsed && activeCount > 0 && (
                          <span style={{
                            padding: '0 5px', borderRadius: '8px', fontSize: '0.55rem',
                            background: `${catInfo?.color || '#6366f1'}25`, color: catInfo?.color,
                            fontWeight: 700, marginLeft: '2px',
                          }}>
                            {activeCount}
                          </span>
                        )}
                        {isCollapsed && (
                          <span style={{ fontSize: '0.55rem', fontWeight: 400, opacity: 0.6, textTransform: 'none' }}>
                            ({catTags.length})
                          </span>
                        )}
                      </button>

                      {/* Tags in category */}
                      {!isCollapsed && (
                        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', padding: '2px 0 4px 0' }}>
                          {catTags.map(tag => (
                            <div
                              key={tag.id}
                              style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}
                            >
                              <button
                                onClick={() => toggleTag(tag.id)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                                  padding: '3px 8px', borderRadius: '16px', fontSize: '0.65rem', fontWeight: 500,
                                  border: `1px solid ${tag.active ? tag.color : 'rgba(255,255,255,0.08)'}`,
                                  background: tag.active ? `${tag.color}18` : 'transparent',
                                  color: tag.active ? tag.color : 'var(--text-muted)',
                                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                                  fontFamily: 'inherit', paddingRight: '18px',
                                }}
                              >
                                {tag.icon} {tag.label}
                              </button>
                              {/* Remove/hide button */}
                              <button
                                onClick={(e) => { e.stopPropagation(); hideTag(tag.id); }}
                                title="Ocultar tag"
                                style={{
                                  position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  width: 14, height: 14, borderRadius: '50%',
                                  border: 'none', background: 'transparent',
                                  color: 'var(--text-muted)', cursor: 'pointer', padding: 0,
                                  opacity: 0.4, transition: 'opacity 0.15s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '0.4'; }}
                              >
                                <X size={8} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <VideoModelPicker
                modality="i2v"
                hideModelSelect={stagingMultiRef}
                value={{ ...stagingVideoPick, durationSeconds: duration }}
                onChange={applyStagingVideoPick}
                durationSeconds={duration}
                onDurationChange={(s) => {
                  setDuration(s);
                  setStagingVideoPick((p) => ({ ...p, durationSeconds: s }));
                }}
                recommendedModelId={stagingModelUseCount > 0 ? stagingRecommendedModelId : null}
                compact
              />

              {/* Input do prompt de vídeo - MOVED UP */}
              <div style={inputBarStyle}>
                <textarea
                  ref={videoTextareaRef}
                  rows={1}
                  value={videoPrompt}
                  onChange={(e) => { handleVideoPromptChange(e.target.value); handleTextareaInput(videoTextareaRef); }}
                  onKeyDown={(e) => { 
                    if (e.key === 'Enter' && !e.shiftKey) { 
                      e.preventDefault(); 
                      handleGenerateVideo(); 
                    } 
                  }}
                  placeholder="Descreva o estilo do vídeo: efeito de drone, câmera lenta, evolução construtiva..."
                  style={textareaStyle}
                />
                <button
                  onClick={handleGenerateVideo}
                  disabled={loading || !videoPrompt}
                  style={sendBtnStyle(!!videoPrompt && !loading)}
                >
                  {storyboard.length === 0 ? <Sparkles size={13} /> : <Video size={13} />}
                </button>
              </div>
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '-0.3rem' }}>
                ~{stagingCreditsEstimate} créditos • Enter para gerar
              </span>

              {/* Storyboard Interativo */}
              {analyzingTags && storyboard.length === 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1rem', background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: '1px dotted rgba(255,255,255,0.1)' }}>
                  <div style={{ width: 14, height: 14, border: '2px solid #818cf8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Gerando storyboard...</span>
                </div>
              )}

              {storyboard.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#818cf8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                      <BookOpen size={11} /> Storyboard do Vídeo
                    </div>
                    <button 
                      onClick={() => analyzeTagsFromPrompt(videoPrompt)}
                      disabled={analyzingTags}
                      style={{ background: 'none', border: 'none', color: '#818cf8', fontSize: '0.62rem', cursor: analyzingTags ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 3, opacity: analyzingTags ? 0.7 : 1 }}
                    >
                      <Sparkles size={10} /> {analyzingTags ? 'Gerando...' : 'Regenerar'}
                    </button>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.62rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.45 }}>
                    Resumo em português + prompt técnico em inglês (editável). Miniatura na primeira fase que citar cada foto.
                  </p>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', padding: '2px' }}>
                    {storyboard.map((phase, idx) => {
                      const catalog = storyboardImageCatalog();
                      const cited = phaseCitedImageIndex(phase);
                      const citedOk = cited != null && cited >= 1 && cited <= catalog.length;
                      const firstCite = citedOk && storyboard.findIndex((p) => phaseCitedImageIndex(p) === cited) === idx;
                      const thumb = firstCite ? catalog[cited - 1] : '';
                      return (
                      <div key={idx} style={{ 
                        background: 'rgba(255,255,255,0.03)', 
                        border: '1px solid rgba(255,255,255,0.08)', 
                        borderRadius: '10px', 
                        padding: '0.75rem 0.85rem',
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'flex-start',
                      }}>
                        {thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            style={{
                              width: 64, height: 86, objectFit: 'cover', flexShrink: 0,
                              borderRadius: 8, background: '#0c0d12',
                              border: '1px solid rgba(255,255,255,0.08)',
                            }}
                          />
                        ) : null}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <div style={{ 
                            width: 22, height: 22, borderRadius: '50%', background: '#6366f120', 
                            color: '#818cf8', fontSize: '0.7rem', display: 'flex', alignItems: 'center', 
                            justifyContent: 'center', fontWeight: 700, border: '1px solid #6366f140', flexShrink: 0
                          }}>
                            {idx + 1}
                          </div>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-light)' }}>{phase.label}</span>
                          {citedOk ? (
                            <span style={{ fontSize: '0.65rem', color: 'rgba(129,140,248,0.85)' }}>← imagem {cited}</span>
                          ) : null}
                        </div>
                        <input
                          type="text"
                          value={phase.description || ''}
                          onChange={(e) => handleUpdateStoryboardPhase(idx, { description: e.target.value })}
                          placeholder="Resumo em português"
                          style={{
                            width: '100%', background: 'transparent', border: 'none',
                            color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem',
                            fontFamily: 'inherit', padding: 0, outline: 'none',
                          }}
                        />
                        <textarea
                          rows={3}
                          onFocus={(e) => {
                            e.currentTarget.style.height = 'auto';
                            e.currentTarget.style.height = `${Math.max(72, e.currentTarget.scrollHeight)}px`;
                          }}
                          ref={(el) => {
                            if (el) {
                              el.style.height = 'auto';
                              el.style.height = `${Math.max(72, el.scrollHeight)}px`;
                            }
                          }}
                          value={phase.prompt_en || ''}
                          onChange={(e) => {
                            handleUpdateStoryboardPhase(idx, { prompt_en: e.target.value });
                            e.target.style.height = 'auto';
                            e.target.style.height = `${Math.max(72, e.target.scrollHeight)}px`;
                          }}
                          placeholder="Prompt técnico em inglês (shot + ação + luz + <<<image_N>>>)"
                          style={{
                            width: '100%', background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.08)', 
                            color: 'var(--text-light)', fontSize: '0.78rem', resize: 'none',
                            lineHeight: '1.45', fontFamily: 'inherit', padding: '10px 12px', borderRadius: '8px',
                            minHeight: '72px', overflow: 'hidden',
                          }}
                        />
                        </div>
                      </div>
                      );
                    })}
                  </div>

                </div>
              )}

            </div>
          </div>
        )}

      </>
    )}

        {/* Legacy sync poll + storyboard animation (async uses slim bar above) */}
        {renderingVideo && storyboard.length > 0 && (
          <div style={{ ...cardStyle, border: '1px solid rgba(99,102,241,0.3)', boxShadow: '0 10px 40px rgba(99,102,241,0.15)', margin: '0 auto', width: '100%' }}>
            <div style={{ ...cardHeaderStyle, background: 'rgba(99,102,241,0.08)', color: '#818cf8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: 14, height: 14, border: '2px solid #818cf8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span>Renderizando Staging Cinematográfico...</span>
              </div>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                Tempo Decorrido: {renderingElapsed}s
              </span>
            </div>
            
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              {/* Progresso Geral */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', background: 'rgba(0,0,0,0.2)', padding: '0.75rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.03)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  <span>Progresso Geral da Renderização</span>
                  <span style={{ color: '#818cf8', fontWeight: 600 }}>
                    {overallProgress}%
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${overallProgress}%`, 
                    height: '100%', 
                    background: 'linear-gradient(90deg, #6366f1, #a855f7)', 
                    borderRadius: '3px',
                    transition: 'width 0.5s ease-out'
                  }} />
                </div>
                <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: '2px' }}>
                  {loadingStep}
                </span>
              </div>

              {/* Fases do Storyboard */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '0.75rem' }}>
                {storyboard.map((phase, idx) => {
                  const isCompleted = idx < activePhaseIdx || (overallProgress === 100);
                  const isActive = idx === activePhaseIdx && overallProgress < 100;
                  const isPending = idx > activePhaseIdx && overallProgress < 100;

                  let cardBg = 'rgba(255,255,255,0.02)';
                  let cardBorder = '1px solid rgba(255,255,255,0.06)';
                  let iconColor = 'rgba(255,255,255,0.2)';
                  let badgeBg = 'rgba(255,255,255,0.05)';
                  let badgeColor = 'var(--text-muted)';
                  let pulseShadow = '';

                  if (isCompleted) {
                    cardBg = 'rgba(34,197,94,0.04)';
                    cardBorder = '1px solid rgba(34,197,94,0.25)';
                    iconColor = '#22c55e';
                    badgeBg = 'rgba(34,197,94,0.1)';
                    badgeColor = '#4ade80';
                  } else if (isActive) {
                    cardBg = 'rgba(99,102,241,0.08)';
                    cardBorder = '1px solid rgba(99,102,241,0.4)';
                    iconColor = '#818cf8';
                    badgeBg = 'rgba(99,102,241,0.15)';
                    badgeColor = '#818cf8';
                    pulseShadow = '0 0 15px rgba(99,102,241,0.15)';
                  }

                  return (
                    <div 
                      key={idx} 
                      style={{ 
                        background: cardBg, 
                        border: cardBorder, 
                        borderRadius: '12px', 
                        padding: '0.85rem', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0.5rem',
                        position: 'relative',
                        boxShadow: pulseShadow,
                        transition: 'all 0.3s ease-in-out',
                        opacity: isPending ? 0.55 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {isCompleted && <CheckCircle2 size={14} style={{ color: iconColor }} />}
                          {isActive && (
                            <div style={{ 
                              width: 12, height: 12, borderRadius: '50%', 
                              border: '2px solid #818cf8', borderTopColor: 'transparent',
                              animation: 'spin 0.8s linear infinite'
                            }} />
                          )}
                          {isPending && <Clock size={12} style={{ color: iconColor }} />}
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? '#fff' : 'var(--text-muted)' }}>
                            Fase {idx + 1}: {phase.label}
                          </span>
                        </div>
                        <span style={{ 
                          padding: '2px 7px', borderRadius: '8px', fontSize: '0.58rem', 
                          fontWeight: 700, background: badgeBg, color: badgeColor
                        }}>
                          {isCompleted && 'Concluído'}
                          {isActive && `Processando ${activePhaseProgress}%`}
                          {isPending && 'Aguardando'}
                        </span>
                      </div>

                      <p style={{ 
                        fontSize: '0.68rem', 
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.45)', 
                        lineHeight: '1.35',
                        margin: 0
                      }}>
                        {phase.description}
                      </p>

                      {isActive && (
                        <div style={{ width: '100%', height: '3px', background: 'rgba(255,255,255,0.05)', borderRadius: '1.5px', overflow: 'hidden', marginTop: '2px' }}>
                          <div style={{ 
                            width: `${activePhaseProgress}%`, 
                            height: '100%', 
                            background: '#818cf8',
                            borderRadius: '1.5px',
                            transition: 'width 0.2s linear'
                          }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        )}

        {jobDeliveredToGallery && !finalVideo && (
          <div style={{
            ...cardStyle,
            border: '1px solid rgba(34,197,94,0.3)',
            maxWidth: '800px',
            margin: '0 auto',
            width: '100%',
          }}>
            <div style={{ ...cardHeaderStyle, background: 'rgba(34,197,94,0.08)', color: '#4ade80' }}>
              <CheckCircle2 size={15} /> Vídeo pronto na galeria
            </div>
            <div style={{ padding: '1rem 1.1rem', fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              A geração terminou. Clique no aviso no canto da tela para assistir, ou abra a <Link to="/galeria" style={{ color: '#4ade80' }}>Galeria de Mídia</Link>.
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            RESULTADO: Vídeo Final
            ═══════════════════════════════════════════════════════════════ */}
        {finalVideo && (
          <div style={{ 
            ...cardStyle,
            border: '1px solid rgba(34,197,94,0.3)', 
            boxShadow: '0 16px 40px rgba(0,0,0,0.35)',
            maxWidth: '800px',
            margin: '0 auto',
            width: '100%'
          }}>
            <div style={{ 
              ...cardHeaderStyle,
              background: 'rgba(34,197,94,0.08)',
              color: '#4ade80' 
            }}>
              <CheckCircle2 size={15} /> Staging Cinematográfico Finalizado
            </div>
            
            <div style={{ position: 'relative', background: '#000', display: 'flex', justifyContent: 'center' }}>
              <video 
                src={finalVideo} 
                controls 
                autoPlay 
                muted 
                loop 
                style={{ 
                  width: '100%', 
                  maxHeight: '420px',
                  objectFit: 'contain',
                  display: 'block' 
                }} 
              />
            </div>

            <div style={{ 
              padding: '1rem', 
              textAlign: 'center',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.15))'
            }}>
              <button 
                className="btn-primary" 
                onClick={resetAll} 
                style={{ 
                  width: 'auto', 
                  padding: '0.55rem 2rem', 
                  fontSize: '0.82rem',
                  borderRadius: '10px',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.2)'
                }}
              >
                <Plus size={15} /> Criar Novo Ambiente
              </button>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {loading && !renderingVideo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.85rem 1rem', background: 'rgba(99,102,241,0.06)', borderRadius: '12px', border: '1px solid rgba(99,102,241,0.12)' }}>
            <div style={{ width: 16, height: 16, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{loadingStep}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '0.65rem', 
            padding: '1rem', 
            background: 'rgba(239,68,68,0.06)', 
            borderRadius: '12px', 
            border: '1px solid rgba(239,68,68,0.12)', 
            fontSize: '0.82rem', 
            color: 'var(--error)',
            boxShadow: '0 4px 12px rgba(239,68,68,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertCircle size={16} /> 
              <span style={{ fontWeight: 500 }}>{error}</span>
            </div>
            
            {error.includes('Pagamentos') && (
              <a href="/pagamento" className="btn-primary" style={{ 
                width: 'fit-content', 
                padding: '0.4rem 1rem', 
                fontSize: '0.75rem',
                textDecoration: 'none',
                background: 'var(--error)',
                marginTop: '0.2rem'
              }}>
                Comprar Créditos
              </a>
            )}

            {error.includes('solicitação') && (
              <a href="/solicitacoes" className="btn-primary" style={{ 
                width: 'fit-content', 
                padding: '0.4rem 1rem', 
                fontSize: '0.75rem',
                textDecoration: 'none',
                background: 'var(--primary)',
                marginTop: '0.2rem'
              }}>
                Abrir Solicitação
              </a>
            )}
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {isTransformModalOpen && (
        <ModalOverlay
          onClose={() => {
            if (!transformingImage && !savingTransform) setIsTransformModalOpen(false);
          }}
          closeOnBackdrop={!transformingImage && !savingTransform}
          style={{
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(10px)',
            zIndex: 9700,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Transformar Imagem com IA"
            onClick={(e) => e.stopPropagation()}
            style={{
            background: '#07070f',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            padding: '1.25rem',
            position: 'relative',
            boxSizing: 'border-box'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '0.65rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#a855f7', fontWeight: 600, fontSize: '0.88rem' }}>
                <Sparkles size={16} />
                <span>Transformar Imagem com IA</span>
              </div>
              <button
                onClick={() => setIsTransformModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: '0.2rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <X size={16} />
              </button>
            </div>

            {/* Preview image */}
            <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', justifyContent: 'center' }}>
              <img
                src={tempTransformedImage || transformCurrentImage()}
                alt={tempTransformedImage ? "Imagem Transformada" : "Imagem Original"}
                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', display: 'block' }}
              />
              {tempTransformedImage && (
                <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(34,197,94,0.85)', color: '#fff', fontSize: '0.62rem', padding: '0.2rem 0.5rem', borderRadius: '6px', fontWeight: 600 }}>
                  ✦ Transformação Concluída
                </div>
              )}
            </div>

            {/* Prompt textarea / used summary */}
            {!tempTransformedImage ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', zIndex: 1 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)' }}>Descreva as alterações ou o estilo desejado:</label>
                <textarea
                  rows={3}
                  value={transformPrompt}
                  onChange={(e) => setTransformPrompt(e.target.value)}
                  placeholder="Ex: Transforme este espaço em uma sala de estar com sofá azul escuro e plantas nas laterais..."
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    padding: '0.75rem',
                    color: '#fff',
                    fontSize: '0.82rem',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ marginTop: '0.4rem', paddingTop: '0.4rem' }}>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>Modelo</div>
                  <ImageModelPicker
                    compact
                    aria-label="Modelo de imagem"
                    value={imageModelId}
                    onChange={setImageModelId}
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', zIndex: 1, padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 500 }}>Prompt Utilizado:</span>
                <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.8)', margin: 0, fontStyle: 'italic' }}>"{transformPrompt}"</p>
              </div>
            )}

            {/* Error inside modal */}
            {transformError && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.65rem 0.8rem',
                background: 'rgba(239, 68, 68, 0.06)',
                border: '1px solid rgba(239, 68, 68, 0.12)',
                borderRadius: '10px',
                color: 'var(--error)',
                fontSize: '0.75rem',
              }}>
                <AlertCircle size={14} />
                <span>{transformError}</span>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              {!tempTransformedImage ? (
                <>
                  <button
                    onClick={() => setIsTransformModalOpen(false)}
                    disabled={transformingImage}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleTransformImage}
                    disabled={transformingImage || !transformPrompt.trim()}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: transformingImage || !transformPrompt.trim() ? 'rgba(99,102,241,0.5)' : '#6366f1',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: transformingImage || !transformPrompt.trim() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      fontFamily: 'inherit',
                    }}
                  >
                    {transformingImage ? (
                      <>
                        <div style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <span>Gerando...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        <span>Gerar com IA ({transformCredits} c.)</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setTempTransformedImage('');
                    }}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      marginRight: 'auto'
                    }}
                  >
                    Regenerar
                  </button>
                  <button
                    onClick={confirmTransform}
                    disabled={savingTransform}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: savingTransform ? 'rgba(34,197,94,0.5)' : '#22c55e',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: savingTransform ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      fontFamily: 'inherit',
                    }}
                  >
                    <Check size={14} />
                    <span>{savingTransform ? 'Salvando...' : 'Confirmar e Salvar'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalOverlay>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      </>
    </div>
    {creationMode === 'staging' && (
      <StagingHistoryRail
        entries={historyEntries}
        activeId={historyActiveId}
        onSelect={restoreStagingHistory}
        onNew={startNewStaging}
      />
    )}
    </div>
  );
}
